import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { createAxiosInstance } from "../utils/Axios";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import ImportLeadsModal from "../components/CRM/ImportLeadsModal";
import {
  useAuth,
  hasModuleAccess,
  hasSectionAccess,
} from "../utils/hooks/useAuth";
import { AllUsers, getCommunityUsers } from "../api";
import EditLeadModal from "./EditLeadModal";
import './css/iurm-lead.css'
import CustomColumnManager from "../components/CustomColumnManager";
import AddLeadModal from "../components/CRM/AddLeadModal";

const renderFormattedNote = (text) => {
  if (!text) return null;

  const dateLookaheadPattern = /(?<=[^\n])\s*(?=\b\d{1,2}(?:st|nd|rd|th)?\s+(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\b|\b\d{1,2}[\/\.-]\d{1,2}(?:[\/\.-]\d{2,4})?\b)/gi;

  const formattedStr = text.replace(dateLookaheadPattern, '\n');
  const lines = formattedStr.split('\n').map(l => l.trim()).filter(Boolean);

  if (lines.length <= 1) {
    return (
      <div style={{ marginTop: '4px', whiteSpace: 'pre-wrap', lineHeight: '1.5', color: '#374151' }}>
        {text}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
      {lines.map((line, idx) => (
        <div 
          key={idx} 
          style={{ 
            padding: '8px 12px', 
            backgroundColor: '#F9FAFB', 
            borderRadius: '6px', 
            borderLeft: '3px solid #7C3AED',
            fontSize: '12px',
            color: '#374151',
            lineHeight: '1.5',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
          }}
        >
          {line}
        </div>
      ))}
    </div>
  );
};

const FOLLOW_UP_ACTION={
  "Call Customer": 0,
  "Not answering calls": 10,
  "Asking callback repeatedly": 20,
  "Shared requirements": 40,
  "Shared floor plan": 50,
  "Discussed budget openly": 60,
  "Asked for pre-estimate": 65,
  "Requested site visit": 75,
  "Site visit completed": 80,
  "Asked for revised estimate": 85,
  "Negotiation ongoing": 90,
  "Discussing start date": 95,
  "Ready to start / Advance": 100,
}

const LEAD_CATEGORY = {
  "Cold Lead": { min: 0, max: 20 },
  "Warm Lead": { min: 21, max: 40 },
  "Interested Lead": { min: 41, max: 60 },
  "Hot Lead": { min: 61, max: 80 },
  "High conversion": { min: 81, max: 100 }
}

function getLeadCategory(score) {
  if (score <= 20) return "Cold Lead";
  if (score <= 40) return "Warm Lead";
  if (score <= 60) return "Interested Lead";
  if (score <= 80) return "Hot Lead";
  return "High conversion";
}

function getStageFromCategory(category) {
  const stageMap = {
    "Cold Lead": "New Lead",
    "Warm Lead": "Qualified",
    "Interested Lead": "Proposal",
    "Hot Lead": "Negotiation",
    "High conversion": "Won"
  };
  return stageMap[category] || "New Lead";
}

const STAGE_STATUS = {
  "New Lead": "New",
  "Qualified": "Qualification in Progress",
  "Proposal": "Contacted",
  "Negotiation": "Contacted",
  "Won": "Contacted",
  "Lost": "Contact Pending",
  "Closed": "Contact Pending"
}

export default function CRM() {
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [viewLeadData, setViewLeadData] = useState(null);
  const [contactLeadData, setContactLeadData] = useState(null);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [notesList, setNotesList] = useState([]);
  const [openDropdownId, setOpenDropdownId] = useState(null);
const [showCustomColumnManager, setShowCustomColumnManager] = useState(false);
const [selectedLeadCategory, setSelectedLeadCategory] = useState("");
const [selectedStage, setSelectedStage] = useState("");
const [selectedStageStatus, setSelectedStageStatus] = useState("");
const [selectedFollowUpAction, setSelectedFollowUpAction] = useState("");
const [selectedWidget, setSelectedWidget] = useState("total");
const [searchTerm, setSearchTerm] = useState("");
const [selectedStatus, setSelectedStatus] = useState("");

// Reset to Page 1 whenever search, status, stage, or widget filter changes
useEffect(() => {
  setCurrentPage(1);
}, [searchTerm, selectedStatus, selectedStage, selectedWidget]);

const [customFields, setCustomFields] = useState([]);
const [isLoadingCustomFields, setIsLoadingCustomFields] = useState(false);
const [showDeleteFieldModal, setShowDeleteFieldModal] = useState(false);
const [fieldToDelete, setFieldToDelete] = useState(null);
const [isDeletingField, setIsDeletingField] = useState(false);
  const { access, loading: authLoading } = useAuth();

  const hasCrmAccess = access ? hasModuleAccess("iurm", access) : false;

  const canViewLeads = access
    ? hasSectionAccess("iurm", "Leads", "view", access)
    : false;
  const canCreateLead = access
    ? hasSectionAccess("iurm", "Leads", "create", access)
    : false;
  const canEditLead = access
    ? hasSectionAccess("iurm", "Leads", "edit", access)
    : false;
  const canDeleteLead = access
    ? hasSectionAccess("iurm", "Leads", "delete", access)
    : false;

  const canViewOverview = access
    ? hasSectionAccess("iurm", "Overview", "view", access)
    : false;
  const canViewContacts = access
    ? hasSectionAccess("iurm", "Contacts", "view", access)
    : false;
  const canViewAccounts = access
    ? hasSectionAccess("iurm", "Accounts", "view", access)
    : false;
  const canViewTasks = access
    ? hasSectionAccess("iurm", "Tasks", "view", access)
    : false;
  const canViewCampaigns = access
    ? hasSectionAccess("iurm", "Campaigns", "view", access)
    : false;
  const canViewIntegration = access
    ? hasSectionAccess("iurm", "Integration", "view", access)
    : false;

  useEffect(() => {
    if (contactLeadData && contactLeadData._id) {
      try {
        const savedNotes = JSON.parse(
          localStorage.getItem("crm_lead_notes") || "{}",
        );
        setNotesList(savedNotes[contactLeadData._id] || []);
      } catch (e) {
        setNotesList([]);
      }
    } else {
      setNotesList([]);
    }
  }, [contactLeadData]);

  const handleSaveNote = () => {
    if (noteText.trim() && contactLeadData && contactLeadData._id) {
      const newNote = {
        text: noteText,
        date: new Date().toLocaleDateString("en-GB"),
      };
      const updatedNotes = [...notesList, newNote];
      setNotesList(updatedNotes);

      try {
        const allSavedNotes = JSON.parse(
          localStorage.getItem("crm_lead_notes") || "{}",
        );
        allSavedNotes[contactLeadData._id] = updatedNotes;
        localStorage.setItem("crm_lead_notes", JSON.stringify(allSavedNotes));
      } catch (e) {
        console.error("Failed to save note", e);
      }
    }
    setIsAddingNote(false);
    setNoteText("");
  };

  const [exportCampaign, setExportCampaign] = useState("");
  const [exportStatus, setExportStatus] = useState("");
  const [exportPayment, setExportPayment] = useState("");
  const [exportType, setExportType] = useState("csv");
  const [isExporting, setIsExporting] = useState(false);
  const [exportData, setExportData] = useState({
    name: true,
    email: true,
    phone: true,
    campaign: true,
    paymentStatus: true,
    submittedDate: true,
  });
  const [isSavingLead, setIsSavingLead] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [newLead, setNewLead] = useState({
    name: "",
    email: "",
    phone: "",
    source: "",
    sourceId: "",
    address: "",
    organization: "",
    campaignId: "",
    launch_30_days: "",
    leadStatus: "New",
    customLeadStatus: "",
    paymentStatus: "Pending",
  });
  const [showEditLeadModal, setShowEditLeadModal] = useState(false);
  const [editLeadData, setEditLeadData] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    to: "",
    template: "",
    subject: "",
    message: "",
    leadName: "",
  });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsappData, setWhatsappData] = useState({
    to: "",
    number: "",
    template: "",
    message: "",
  });
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [smsData, setSmsData] = useState({
    to: "",
    number: "",
    template: "",
    message: "",
    leadName: "",
  });
  const [isSendingSMS, setIsSendingSMS] = useState(false);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [meetingLink, setMeetingLink] = useState("");
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [showCopied, setShowCopied] = useState(false);

  const [showCopyLinkModal, setShowCopyLinkModal] = useState(false);
  const [copyLinkUrl, setCopyLinkUrl] = useState("");
  const [showServiceRequestModal, setShowServiceRequestModal] = useState(false);
  const [serviceRequestData, setServiceRequestData] = useState({
    leadId: "",
    leadName: "",
    campaignTitle: "",
    phone: "",
    assignTo: "",
    serviceType: "",
    priority: "Medium",
    notes: "",
    dueDate: "",
    communityId: "",
  });
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [isCreatingServiceRequest, setIsCreatingServiceRequest] =
    useState(false);
  const [showReferralAnalyticsModal, setShowReferralAnalyticsModal] =
    useState(false);
  const [referralAnalyticsData, setReferralAnalyticsData] = useState({
    leadName: "",
    referralCode: "",
  });
  const [showSingleExportModal, setShowSingleExportModal] = useState(false);
  const [singleExportType, setSingleExportType] = useState("csv");
  const [singleExportLead, setSingleExportLead] = useState(null);
  const [isSingleExporting, setIsSingleExporting] = useState(false);
  const [showDeleteLeadModal, setShowDeleteLeadModal] = useState(false);
  const [deleteLeadId, setDeleteLeadId] = useState(null);
  const [isDeletingLead, setIsDeletingLead] = useState(false);
  const [showFloatingFilter, setShowFloatingFilter] = useState(false);
  const [showFloatingView, setShowFloatingView] = useState(false);
  const [viewMode, setViewMode] = useState("table");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [allusers, setAllUsers] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  
const [currentPage, setCurrentPage] = useState(1);
const [rowsPerPage, setRowsPerPage] = useState(10);
const [totalLeads, setTotalLeads] = useState(0);

  useEffect(() => {
    const apiHelper = async () => {
      try {
        if(!access?.tenantId)return
        const data = await getCommunityUsers(access?.tenantId);
        const allUserss=data?.data.map((val)=>val?.userId)
        setAllUsers(allUserss);
      } catch (error) {}
    };
    apiHelper();
  }, [access]);

  const fetchCommunityUsersAndServices = async (communityId) => {
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl =
        process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";

      const [usersRes, servicesRes] = await Promise.all([
        axiosInstance.get(`${apiUrl}/communities/users/${communityId}`),
        axiosInstance.get(`${apiUrl}/communities/services/${communityId}`),
      ]);

      if (usersRes.data && usersRes.data.data) {
        setTeamMembers(usersRes.data.data);
      }

      if (servicesRes.data && servicesRes.data.services) {
        setServiceOptions(servicesRes.data.services);
      }
    } catch (error) {
      console.error("Failed to fetch ecosystem data", error);
    }
  };

  const [leads, setLeads] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const leadIdParam = searchParams.get("leadId");
  const processedLeadIdRef = useRef(null);

  const handleCloseViewLeadModal = () => {
    setViewLeadData(null);
    if (searchParams.get("leadId")) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("leadId");
      setSearchParams(newParams, { replace: true });
    }
  };

  // Auto-open Lead Details Modal when ?leadId= is present in URL
  useEffect(() => {
    if (!leadIdParam) return;
    if (processedLeadIdRef.current === leadIdParam) return;
    processedLeadIdRef.current = leadIdParam;

    const paramUpper = leadIdParam.trim().toUpperCase();

    const findMatchingLead = (leadList) => {
      if (!leadList || !Array.isArray(leadList) || leadList.length === 0) return null;
      return leadList.find((l) => {
        let rawId = typeof l._id === "string" ? l._id : (l._id?.$oid || l.id || String(l._id || ""));
        const fullId = String(rawId).toUpperCase();
        const shortId = fullId.length >= 6 ? fullId.slice(-6) : fullId;
        return fullId === paramUpper || shortId === paramUpper;
      });
    };

    // 1. Try finding in current loaded leads state
    const foundInCurrent = findMatchingLead(leads);
    if (foundInCurrent) {
      setViewLeadData(foundInCurrent);
      return;
    }

    // 2. Fetch direct single lead or full leads list from API to locate lead across all pages
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";
      const axiosInstance = createAxiosInstance();

      axiosInstance
        .get(`${apiUrl}/leads/${leadIdParam}`)
        .then((res) => {
          const singleLead = res.data?.lead || res.data?.data || (res.data?._id ? res.data : null);
          if (singleLead && (singleLead._id || singleLead.id)) {
            setViewLeadData(singleLead);
          } else {
            return axiosInstance.get(`${apiUrl}/leads?limit=2000`);
          }
        })
        .then((res) => {
          if (res && res.data) {
            const allLeads = res.data?.leads || res.data?.data || (Array.isArray(res.data) ? res.data : []);
            const found = findMatchingLead(allLeads);
            if (found) setViewLeadData(found);
          }
        })
        .catch((err) => {
          axiosInstance
            .get(`${apiUrl}/leads?limit=2000`)
            .then((res) => {
              const allLeads = res.data?.leads || res.data?.data || (Array.isArray(res.data) ? res.data : []);
              const found = findMatchingLead(allLeads);
              if (found) setViewLeadData(found);
            })
            .catch((e) => console.error("Error finding lead for leadIdParam:", e));
        });
    } catch (err) {
      console.error("Error in leadIdParam auto-open effect:", err);
    }
  }, [leadIdParam]);




  const [stats, setStats] = useState({
    totalLeads: 0,
    qualifiedLeads:0,
    proposalsShared:0,
    negotiations:0,
    wonLeads:0,
    lostLeads:0,
    avgLeadAge:0,
    avgFollowUpCount:0,
    winRate:'0',
  });

  const [campaignsList, setCampaignsList] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [selectedSource, setSelectedSource] = useState("");
  const [showColumnToggle, setShowColumnToggle] = useState(false);

  const defaultVisibleColumns = {
    leadId: true,
    lead: true,
    createdTime: true,
    campaignId: true,
    campaignName: true,
    source: true,
    sourceId: true,
    email: true,
    phone: true,
    status: true,
    budget: true,
    probability: true,
    followUpCount: true,
    nextAction: true,
    nextActionDueDate: true,
    remarks: true,
    assignedTo: true,
    owner: true,
    action: true
  };

  const defaultColumnNames = {
    leadId: "LEAD ID",
    lead: "LEAD",
    fullName: "FULL NAME",
    createdTime: "CREATE TIME",
    campaignId: "CAMPAIGN ID",
    campaignName: "CAMPAIGN NAME",
    source: "SOURCE",
    sourceId: "WHEN DO YOU WANT TO START YOUR INTERIOR",
    email: "EMAIL",
    phone: "PHONE",
    status: "STATUS",
    stage: "STAGE",
    stageStatus: "STAGE STATUS",
    budget: "BUDGET",
    probability: "PROBABILITY",
    followUpCount: "FOLLOW UP COUNT",
    followUpAction: "FOLLOW UP ACTION",
    nextAction: "NEXT ACTION",
    nextActionDueDate: "NEXT ACTION DUE DATE",
    remarks: "REMARKS*",
    assignedTo: "ASSIGNED TO",
    owner: "OWNER",
    action: "ACTION"
  };

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem("crm_visible_columns");
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...defaultVisibleColumns,
          ...parsed,
          createdTime: parsed.createdTime !== undefined ? parsed.createdTime : true
        };
      }
    } catch (e) {
      console.error("Failed to parse saved visibleColumns:", e);
    }
    return defaultVisibleColumns;
  });

  const [columnNames, setColumnNames] = useState(() => {
    try {
      const saved = localStorage.getItem("crm_column_names");
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error("Failed to parse saved columnNames:", e);
    }
    return defaultColumnNames;
  });

  useEffect(() => {
    try {
      localStorage.setItem("crm_visible_columns", JSON.stringify(visibleColumns));
    } catch (e) {}
  }, [visibleColumns]);

  useEffect(() => {
    try {
      localStorage.setItem("crm_column_names", JSON.stringify(columnNames));
    } catch (e) {}
  }, [columnNames]);

  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      const saved = localStorage.getItem("crm_column_order");
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return [
      "leadId",
      "createdTime",
      "campaignId",
      "campaignName",
      "lead",
      "source",
      "sourceId",
      "status",
      "budget",
      "probability",
      "followUpCount",
      "remarks",
      "assignedTo",
      "owner",
      "nextAction",
      "nextActionDueDate",
      "action"
    ];
  });

  useEffect(() => {
    try {
      localStorage.setItem("crm_column_order", JSON.stringify(columnOrder));
    } catch (e) {}
  }, [columnOrder]);

  const [draggedColIndex, setDraggedColIndex] = useState(null);
  const [dragOverColIndex, setDragOverColIndex] = useState(null);

  const moveColumnItem = (fromIndex, toIndex) => {
    setColumnOrder((prevOrder) => {
      const allCols = Array.from(new Set([
        ...prevOrder,
        ...Object.keys(defaultColumnNames),
        ...Object.keys(visibleColumns),
        ...customFields.map(f => `custom_${f.fieldKey}`)
      ]));
      if (fromIndex < 0 || fromIndex >= allCols.length || toIndex < 0 || toIndex >= allCols.length) {
        return prevOrder;
      }
      const updated = [...allCols];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  };

  const standardColumns = [
    "leadId",
    "remarks",
    "action",
    "assignedTo",
    "followUpCount",
    "lead",
    "budget",
    "probability", 
    "owner"
  ];

  const [columnRefresh, setColumnNamesRefresh] = useState(false);
  useEffect(() => {
    const fetchCustomFields = async () => {
      if (!access?.tenantId) return;
      
      setIsLoadingCustomFields(true);
      try {
        const axiosInstance = createAxiosInstance();
        const apiUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";
        
        const response = await axiosInstance.get(`${apiUrl}/leads/field?tenantId=${access?.tenantId}`);
        
        if (response.data && response.data.success) {
          const fields = response.data.fields || [];
          setCustomFields(fields);
          
          fields.forEach(field => {
            const colKey = `custom_${field.fieldKey}`;
            const label = (field.fieldName || field.fieldKey).toUpperCase();
            setColumnNames(prev => ({ [colKey]: prev[colKey] || label, ...prev }));
            setVisibleColumns(prev => ({
              ...prev,
              [colKey]: prev[colKey] !== undefined ? prev[colKey] : true
            }));
          });
        }
      } catch (error) {
        console.error('Failed to fetch custom fields:', error);
      } finally {
        setIsLoadingCustomFields(false);
      }
    };
    
    fetchCustomFields();
  }, [access?.tenantId, columnRefresh]);

  useEffect(() => {
    if (!leads || leads.length === 0) return;

    const discoveredKeys = new Set();

    (customFields || []).forEach(f => {
      if (f.fieldKey) discoveredKeys.add(f.fieldKey);
    });

    leads.forEach(lead => {
      if (lead.customFields && typeof lead.customFields === 'object') {
        Object.keys(lead.customFields).forEach(k => discoveredKeys.add(k));
      }
      if (lead.formData && typeof lead.formData === 'object') {
        Object.keys(lead.formData).forEach(k => {
          if (!['name', 'email', 'phone', 'address', 'remarks', 'organization'].includes(k)) {
            discoveredKeys.add(k);
          }
        });
      }
    });

    if (discoveredKeys.size === 0) return;

    setColumnNames(prev => {
      const next = { ...prev };
      let changed = false;
      discoveredKeys.forEach(k => {
        const colKey = `custom_${k}`;
        if (!next[colKey]) {
          next[colKey] = k.replace(/_/g, ' ').toUpperCase();
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    setVisibleColumns(prev => {
      const next = { ...prev };
      let changed = false;
      discoveredKeys.forEach(k => {
        const colKey = `custom_${k}`;
        if (next[colKey] === undefined) {
          next[colKey] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [leads, customFields]);

  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [draftColumnName, setDraftColumnName] = useState("");
  const handleAddCustomColumn = () => {
    setIsAddingColumn(true);
    setDraftColumnName("");
  };
  const [selectedPayment, setSelectedPayment] = useState("");

  const leadStages = [
    "New",
    "Contracted",
    "Interested",
    "Qualified",
    "Lost",
    "Converted",
    "Others",
  ];

  const displayedLeads = useMemo(() => {
    return leads.filter((lead) => {
      if (selectedWidget && selectedWidget !== "total") {
        const status = (lead.leadStatus || "New").toLowerCase();
        const followUpAction = lead.followUpAction || "Call Customer";
        const score = FOLLOW_UP_ACTION[followUpAction] || 0;
        const leadCategory = getLeadCategory(score);
        const computedStage = (getStageFromCategory(leadCategory) || "").toLowerCase();
        const stage = (lead.stage || computedStage).toLowerCase();

        if (selectedWidget === "qualified") {
          if (status !== "qualified" && stage !== "qualified") return false;
        } else if (selectedWidget === "proposal") {
          if (status !== "proposal" && stage !== "proposal" && !lead.proposalShared) return false;
        } else if (selectedWidget === "negotiation") {
          if (!status.includes("negotiat") && !stage.includes("negotiat")) return false;
        } else if (selectedWidget === "won") {
          if (status !== "won" && status !== "converted" && stage !== "won") return false;
        } else if (selectedWidget === "lost") {
          if (status !== "lost" && stage !== "lost") return false;
        }
      }
      return true;
    });
  }, [leads, selectedWidget]);

  const computedStats = useMemo(() => {
    return {
      totalLeads: stats.totalLeads || totalLeads || 0,
      qualifiedLeads: stats.qualifiedLeads || 0,
      proposalsShared: stats.proposalsShared || 0,
      negotiations: stats.negotiations || 0,
      wonLeads: stats.wonLeads || 0,
      lostLeads: stats.lostLeads || 0,
      winRate: stats.winRate || "0%",
      avgLeadAge: stats.avgLeadAge || 0,
      avgFollowUpCount: stats.avgFollowUpCount || 0,
    };
  }, [stats, totalLeads]);

  const handleDragStart = (e, leadId) => {
    e.dataTransfer.setData("leadId", leadId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData("leadId");
    if (!leadId) return;

    const originalLeads = [...leads];
    setLeads((prevLeads) =>
      prevLeads.map((lead) =>
        lead._id === leadId ? { ...lead, leadStatus: targetStage } : lead,
      ),
    );

    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl =
        process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";
      await axiosInstance.put(`${apiUrl}/leads/${leadId}`, {
        leadStatus: targetStage,
      });
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to update lead status", error);
      toast.error("Failed to update lead status");
      setLeads(originalLeads);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingData(true);
      try {
        const axiosInstance = createAxiosInstance();
        const apiUrl =
          process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";

        let queryParams = `?limit=${rowsPerPage}&page=${currentPage}`;
        if (selectedCampaign) queryParams += `&campaignId=${selectedCampaign}`;
        if (selectedSource) queryParams += `&source=${selectedSource}`;
        if (selectedStatus) queryParams += `&status=${selectedStatus}`;
        if (selectedPayment) queryParams += `&paymentStatus=${selectedPayment}`;
        if (selectedLeadCategory) queryParams += `&leadCategory=${selectedLeadCategory}`;      
        if (selectedStage) queryParams += `&stage=${selectedStage}`;                          
        if (selectedStageStatus) queryParams += `&stageStatus=${selectedStageStatus}`;        
        if (selectedFollowUpAction) queryParams += `&followUpAction=${selectedFollowUpAction}`; 
        if (searchTerm) queryParams += `&search=${searchTerm}`;
        if (startDate) queryParams += `&startDate=${startDate}`;
        if (endDate) queryParams += `&endDate=${endDate}`;
        queryParams += `&_t=${new Date().getTime()}`;

        const { data } = await axiosInstance.get(
          `${apiUrl}/leads${queryParams}`,
        );

        if (data && data.success) {
          if (data.leads) {
            setLeads(data.leads);
            setTotalLeads(data.total || data.leads.length);
          }
          if (data.stats) {
            setStats((prevStats) => ({
              ...data.stats,
              totalLeads: Math.max(prevStats.totalLeads || 0, data.total || 0, data.stats.totalLeads || 0),
              qualifiedLeads: Math.max(prevStats.qualifiedLeads || 0, data.stats.qualifiedLeads || 0),
              proposalsShared: Math.max(prevStats.proposalsShared || 0, data.stats.proposalsShared || 0),
              negotiations: Math.max(prevStats.negotiations || 0, data.stats.negotiations || 0),
              wonLeads: Math.max(prevStats.wonLeads || 0, data.stats.wonLeads || 0),
              lostLeads: Math.max(prevStats.lostLeads || 0, data.stats.lostLeads || 0),
            }));
          }
        }

        if (campaignsList.length === 0) {
          const campRes = await axiosInstance.get(`${apiUrl}/campaigns`);
          if (campRes.data) {
            setCampaignsList(campRes.data);
          }
        }
      } catch (error) {
        console.error("Failed to fetch data from API.", error);
        toast.error(error?.response?.data?.message || "Failed to fetch leads");
      } finally {
        setIsLoadingData(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchData();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [
    selectedCampaign,
    selectedSource,
    selectedStatus,
    selectedPayment,
    selectedLeadCategory,  
    selectedStage,          
    selectedStageStatus,    
    selectedFollowUpAction, 
    refreshTrigger,
    searchTerm,
    startDate,
    endDate,
    currentPage,
    rowsPerPage
  ]);

  const getLeadCellValue = (lead, key) => {
    if (key.startsWith("custom_")) {
      const fieldKey = key.replace("custom_", "");
      const val =
        lead.customFields?.[fieldKey] ??
        lead.formData?.[fieldKey] ??
        lead[fieldKey] ??
        "-";
      return val !== null && val !== undefined && val !== "" ? val : "-";
    }

    switch (key) {
      case "leadId":
        return lead._id ? lead._id.toString().slice(-6).toUpperCase() : "-";
      case "lead":
      case "leadName":
        return lead.formData?.name || lead.userId?.name || "-";
      case "email":
        return lead.formData?.email || lead.userId?.email || "-";
      case "phone":
        return lead.formData?.phone || lead.userId?.mobile || "-";
      case "source":
        return lead.source || "-";
      case "sourceId":
        return lead.sourceId || lead.formData?.sourceId || "-";
      case "address":
        return lead.formData?.address || "-";
      case "organization":
        return lead.formData?.organization || "-";
      case "status":
      case "leadStatus":
        return lead.leadStatus || "New";
      case "payment":
      case "paymentStatus":
        return lead.paymentStatus || "Pending";
      case "remarks":
        return lead.remarks || lead.formData?.remarks || "-";
      case "budget":
        return lead.formData?.budget || (lead.amount ? `₹${lead.amount}` : "₹0");
      case "probability":
        return lead.probability !== undefined && lead.probability !== null
          ? `${lead.probability}%`
          : "0%";
      case "followUpCount":
        return lead.followUpCount !== undefined ? lead.followUpCount : 0;
      case "assignedTo":
        return lead.assignedTo?.name || lead.assignedTo?.email || "-";
      case "owner":
        return lead.createdBy || "-";
      case "createdTime":
        return lead.createdTime || (lead.createdAt ? format(new Date(lead.createdAt), "dd MMM yyyy") : "-");
      default:
        const val = lead[key] ?? lead.formData?.[key] ?? "-";
        return val !== null && val !== undefined && val !== "" ? val : "-";
    }
  };

  const getDynamicHeadersAndRows = (leadList) => {
    const activeColKeys = Object.keys(visibleColumns).filter(
      (key) => visibleColumns[key] && key !== "action"
    );

    const headers = activeColKeys.map(
      (key) => columnNames[key] || key.replace("custom_", "").toUpperCase()
    );

    const rows = leadList.map((lead) =>
      activeColKeys.map((key) => getLeadCellValue(lead, key))
    );

    return { headers, rows };
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl =
        process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";

      let queryParams = `?limit=100000&page=1`;
      if (exportCampaign) queryParams += `&campaignId=${exportCampaign}`;
      if (exportStatus) queryParams += `&status=${exportStatus}`;
      if (exportPayment) queryParams += `&paymentStatus=${exportPayment}`;

      const { data } = await axiosInstance.get(`${apiUrl}/leads${queryParams}`);

      if (!data || !data.success || !data.leads || data.leads.length === 0) {
        toast.warning("No leads found to export.");
        setIsExporting(false);
        return;
      }

      const allLeads = data.leads;
      const exportStats = data.stats;
      const { headers, rows } = getDynamicHeadersAndRows(allLeads);

      const campaignName = exportCampaign
        ? campaignsList.find((c) => c._id === exportCampaign)?.title ||
          "Selected Campaign"
        : "All Campaigns";

      if (exportType === "csv") {
        const csvContent = [headers.join(",")]
          .concat(
            rows.map((row) =>
              row
                .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
                .join(","),
            ),
          )
          .join("\n");
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `leads-report-${format(new Date(), "yyyyMMdd")}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (exportType === "xlsx") {
        const worksheetData = [headers, ...rows];
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
        XLSX.writeFile(
          workbook,
          `leads-report-${format(new Date(), "yyyyMMdd")}.xlsx`,
        );
      } else if (exportType === "pdf") {
        const doc = new jsPDF();
        doc.setFontSize(20);
        doc.setTextColor(40);
        doc.text("LEADS REPORT", 14, 22);
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Campaign: ${campaignName}`, 14, 32);
        doc.text(`Export Date: ${format(new Date(), "dd MMM yyyy")}`, 14, 38);

        autoTable(doc, {
          head: [headers],
          body: rows,
          startY: 45,
          theme: "grid",
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: { fillColor: [80, 20, 208] },
        });

        const finalY = doc.lastAutoTable?.finalY || 45;
        doc.setFontSize(11);
        doc.setTextColor(40);
        doc.text(
          `Total Leads: ${exportStats?.totalLeads || allLeads.length}`,
          14,
          finalY + 10,
        );
        doc.text(`Paid Leads: ${exportStats?.paidLeads || 0}`, 14, finalY + 16);
        doc.text(
          `Conversion Rate: ${exportStats?.conversionRate || "0%"}`,
          14,
          finalY + 22,
        );
        doc.setFontSize(9);
        doc.setTextColor(150);
        doc.text(
          "Generated by iXES CRM",
          14,
          doc.internal.pageSize.height - 10,
        );
        doc.save(`leads-report-${format(new Date(), "yyyyMMdd")}.pdf`);
      }

      setShowExportModal(false);
      toast.success("Export successful!");
    } catch (error) {
      console.error("Export failed", error);
      toast.error("Failed to export leads.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleSingleExport = () => {
    setIsSingleExporting(true);
    setTimeout(() => {
      if (!singleExportLead) {
        setIsSingleExporting(false);
        return;
      }

      const leadsToExport =
        singleExportLead === "bulk"
          ? leads.filter((l) => selectedLeads.includes(l._id))
          : [singleExportLead];

      if (leadsToExport.length === 0) {
        setIsSingleExporting(false);
        return;
      }

      const { headers, rows: rowsData } = getDynamicHeadersAndRows(leadsToExport);

      const fileName =
        singleExportLead === "bulk"
          ? `bulk_leads_export_${new Date().toISOString().split("T")[0]}`
          : `lead_export_${(leadsToExport[0].formData?.name || "unknown").replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}`;

      if (singleExportType === "csv") {
        const csvContent = [headers.join(",")]
          .concat(
            rowsData.map((row) =>
              row
                .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
                .join(","),
            ),
          )
          .join("\n");
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${fileName}.csv`;
        link.click();
      } else if (singleExportType === "excel") {
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rowsData]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
        XLSX.writeFile(workbook, `${fileName}.xlsx`);
      } else if (singleExportType === "pdf") {
        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text(
          singleExportLead === "bulk"
            ? `Bulk Leads Export (${leadsToExport.length} Leads)`
            : "Lead Export",
          14,
          20,
        );
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 28);

        autoTable(doc, {
          head: [headers],
          body: rowsData,
          startY: 35,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [124, 58, 237] },
        });

        doc.save(`${fileName}.pdf`);
      }

      toast.success(
        singleExportLead === "bulk"
          ? `${leadsToExport.length} leads exported successfully!`
          : "Lead exported successfully!",
      );
      setIsSingleExporting(false);
      setShowSingleExportModal(false);
      if (singleExportLead === "bulk") setSelectedLeads([]);
    }, 800);
  };

  const handleDeleteLead = async (deleteAll = false) => {
    const isDeleteAll = deleteAll === true || deleteLeadId === "all";
    const idsToDelete =
      deleteLeadId === "bulk" ? selectedLeads : [deleteLeadId];

    if (!isDeleteAll && idsToDelete.length === 0) return;

    setIsDeletingLead(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl =
        process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";

      const payload = isDeleteAll
        ? { deleteAll: true }
        : { leadIds: idsToDelete };

      const res = await axiosInstance.post(`${apiUrl}/leads/delete-bulk`, payload);

      if (res.data && res.data.success) {
        if (isDeleteAll) {
          const count = res.data.deletedCount || totalLeads || leads.length;
          toast.success(`Successfully deleted all ${count} lead(s)!`);
          setLeads([]);
          setTotalLeads(0);
          setStats((prev) => ({ ...prev, totalLeads: 0 }));
          setSelectedLeads([]);
        } else {
          toast.success(`Successfully deleted ${idsToDelete.length} lead(s)!`);
          setLeads((prevLeads) =>
            prevLeads.filter((l) => !idsToDelete.includes(l._id)),
          );
          setTotalLeads((prev) => Math.max(0, prev - idsToDelete.length));
          setSelectedLeads((prev) =>
            prev.filter((id) => !idsToDelete.includes(id)),
          );
        }
        setShowDeleteLeadModal(false);
        setRefreshTrigger((prev) => prev + 1);
      } else {
        toast.error(res.data?.message || "Failed to delete lead(s)");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete lead(s).");
    } finally {
      setIsDeletingLead(false);
      setDeleteLeadId(null);
    }
  };

  const handleBulkMarkPaid = async () => {
    if (selectedLeads.length === 0) return;
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl =
        process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";

      setLeads((prevLeads) =>
        prevLeads.map((lead) => {
          if (selectedLeads.includes(lead._id)) {
            return { ...lead, paymentStatus: "Successful" };
          }
          return lead;
        }),
      );

      await Promise.all(
        selectedLeads.map((id) =>
          axiosInstance
            .put(`${apiUrl}/leads/${id}`, { paymentStatus: "Successful" })
            .catch((e) => console.error(e)),
        ),
      );

      toast.success(`${selectedLeads.length} leads marked as paid!`);
      setSelectedLeads([]);
    } catch (error) {
      toast.error("Failed to mark paid.");
    }
  };

  const handleSaveLead = async () => {
    if (!newLead.name || !newLead.email || !newLead.phone) {
      toast.error("Please fill required fields: Name, Email, and Phone No");
      return;
    }

    setIsSavingLead(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl =
        process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";

      const formData = {
        name: newLead.name,
        email: newLead.email,
        phone: newLead.phone,
        address: newLead.address,
        organization: newLead.organization,
        launch_30_days: newLead.launch_30_days,
      };

      await axiosInstance.post(`${apiUrl}/leads`, {
        campaignId: newLead.campaignId || undefined,
        source: newLead.source,
        sourceId: newLead.sourceId,
        formData,
        leadStatus:
          newLead.leadStatus === "Others"
            ? newLead.customLeadStatus
            : newLead.leadStatus,
        paymentStatus: newLead.paymentStatus,
      });

      toast.success("Lead created successfully!");
      setShowAddLeadModal(false);
      setNewLead({
        name: "",
        email: "",
        phone: "",
        source: "",
        sourceId: "",
        address: "",
        organization: "",
        campaignId: "",
        launch_30_days: "",
        leadStatus: "New",
        customLeadStatus: "",
        paymentStatus: "Pending",
      });
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.log("Failed to save lead", error);
      toast.error("Failed to create lead.");
    } finally {
      setIsSavingLead(false);
    }
  };

  const handleUpdateLead = async () => {
    if (!editLeadData.name || !editLeadData.email || !editLeadData.phone) {
      toast.error("Please fill required fields: Name, Email, and Phone No");
      return;
    }

    setIsSavingEdit(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl =
        process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";

      const formData = {
        name: editLeadData.name,
        email: editLeadData.email,
        phone: editLeadData.phone,
        address: editLeadData.address,
        organization: editLeadData.organization,
        notes: editLeadData.notes,
      };

      setLeads((prevLeads) =>
        prevLeads.map((lead) => {
          if (lead._id === editLeadData._id) {
            return {
              ...lead,
              formData: {
                ...lead.formData,
                ...formData,
              },
              leadStatus:
                editLeadData.leadStatus === "Others"
                  ? editLeadData.customLeadStatus
                  : editLeadData.leadStatus,
              paymentStatus: editLeadData.paymentStatus,
              amount: editLeadData.amount,
            };
          }
          return lead;
        }),
      );

      try {
        await axiosInstance.put(`${apiUrl}/leads/${editLeadData._id}`, {
          formData,
          leadStatus:
            editLeadData.leadStatus === "Others"
              ? editLeadData.customLeadStatus
              : editLeadData.leadStatus,
          paymentStatus: editLeadData.paymentStatus,
          amount: editLeadData.amount,
        });
      } catch (backendError) {
        console.warn(
          "Backend PUT failed or not implemented yet. Local state updated successfully.",
        );
      }

      if (editLeadData.notes !== undefined) {
        try {
          const allSavedNotes = JSON.parse(
            localStorage.getItem("crm_lead_notes") || "{}",
          );
          allSavedNotes[editLeadData._id] = [
            {
              text: editLeadData.notes,
              date: new Date().toLocaleDateString("en-GB"),
            },
          ];
          localStorage.setItem("crm_lead_notes", JSON.stringify(allSavedNotes));
        } catch (e) {}
      }

      toast.success("Lead updated successfully!");
      setShowEditLeadModal(false);
    } catch (error) {
      console.error("Failed to update lead", error);
      toast.error("Failed to update lead.");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailData.subject || !emailData.message) {
      toast.error("Subject and Message are required");
      return;
    }
    setIsSendingEmail(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl =
        process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";

      await axiosInstance.post(`${apiUrl}/leads/send-email`, {
        to: emailData.to,
        subject: emailData.subject,
        message: emailData.message,
      });

      toast.success("Email sent successfully!");
      setShowEmailModal(false);
    } catch (error) {
      console.error("Failed to send email", error);
      toast.error("Failed to send email. Please try again.");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleTemplateChange = (e) => {
    const template = e.target.value;
    const name = emailData.leadName || "";

    if (template === "Payment Reminder") {
      setEmailData((prev) => ({
        ...prev,
        template,
        subject: "Payment Reminder",
        message: `Hi ${name},\n\nThank you for registering.\nYour payment is still pending.\n\nPlease complete payment here:\nhttps://ixes.ai/pay/9283`,
      }));
    } else if (template === "Registration Confirmation") {
      setEmailData((prev) => ({
        ...prev,
        template,
        subject: "Registration Confirmation",
        message: `Hi ${name},\n\nYour registration has been successfully confirmed!\n\nWelcome to our platform.`,
      }));
    } else if (template === "Welcome Email") {
      setEmailData((prev) => ({
        ...prev,
        template,
        subject: "Welcome to iXES",
        message: `Hi ${name},\n\nWelcome to iXES! We are thrilled to have you on board.\n\nLet us know if you have any questions.`,
      }));
    } else if (template === "Donation Success") {
      setEmailData((prev) => ({
        ...prev,
        template,
        subject: "Donation Received successfully",
        message: `Hi ${name},\n\nThank you for your generous donation. Your support means everything to us!`,
      }));
    } else {
      setEmailData((prev) => ({ ...prev, template, subject: "", message: "" }));
    }
  };

  const handleWhatsAppTemplateChange = (e) => {
    const template = e.target.value;
    const name = whatsappData.to || "";

    if (template === "Message 1 (Registration)") {
      setWhatsappData((prev) => ({
        ...prev,
        template,
        message: `Hi ${name},\nThank you for registering.\nPlease complete your payment.`,
      }));
    } else if (template === "Message 2 (Pending Payment)") {
      setWhatsappData((prev) => ({
        ...prev,
        template,
        message: `Hi ${name},\nJust a friendly reminder that your payment is pending.\nPlease let us know if you need any assistance.`,
      }));
    } else {
      setWhatsappData((prev) => ({ ...prev, template, message: "" }));
    }
  };

  const handleOpenWhatsApp = () => {
    if (!whatsappData.number || !whatsappData.message) {
      toast.error("Phone number and message are required");
      return;
    }

    let formattedNumber = whatsappData.number.replace(/[^\d+]/g, "");
    if (formattedNumber.startsWith("+")) {
      formattedNumber = formattedNumber.substring(1);
    }

    const encodedMessage = encodeURIComponent(whatsappData.message);
    const whatsappUrl = `https://wa.me/${formattedNumber}?text=${encodedMessage}`;

    window.open(whatsappUrl, "_blank");
    setShowWhatsAppModal(false);
  };

  const handleSMSTemplateChange = (e) => {
    const template = e.target.value;
    const name = smsData.leadName || "";

    if (template === "Message 1 (Registration)") {
      setSmsData((prev) => ({
        ...prev,
        template,
        message: `Hi ${name},\nThank you for registering.`,
      }));
    } else if (template === "Message 2 (Pending Payment)") {
      setSmsData((prev) => ({
        ...prev,
        template,
        message: `Hi ${name},\nJust a friendly reminder that your payment is pending.\nPlease let us know if you need any assistance.`,
      }));
    } else {
      setSmsData((prev) => ({ ...prev, template, message: "" }));
    }
  };

  const handleSendSMS = async () => {
    if (!smsData.number || !smsData.message) {
      toast.error("Phone number and message are required");
      return;
    }

    setIsSendingSMS(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl =
        process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";

      let formattedNumber = smsData.number.replace(/[^\d+]/g, "");
      if (!formattedNumber.startsWith("+")) {
        formattedNumber = "+" + formattedNumber;
      }

      await axiosInstance.post(`${apiUrl}/leads/send-sms`, {
        to: formattedNumber,
        message: smsData.message,
      });

      toast.success("SMS sent successfully!");
      setShowSMSModal(false);
    } catch (error) {
      console.error("Failed to send SMS", error);
      toast.error(
        `Failed to send SMS: ${error.response?.data?.error || error.message}`,
      );
    } finally {
      setIsSendingSMS(false);
    }
  };

  const handleQuickUpdate = async (leadToUpdate, field, newValue) => {
    setOpenDropdownId(null);

    setLeads((prevLeads) =>
      prevLeads.map((lead) => {
        if (lead._id === leadToUpdate._id) {
          return { ...lead, [field]: newValue };
        }
        return lead;
      }),
    );

    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl =
        process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";

      const payload = {
        formData: leadToUpdate.formData,
        leadStatus: field === "leadStatus" ? newValue : leadToUpdate.leadStatus,
        paymentStatus:
          field === "paymentStatus" ? newValue : leadToUpdate.paymentStatus,
        amount: leadToUpdate.amount,
      };

      await axiosInstance.put(`${apiUrl}/leads/${leadToUpdate._id}`, payload);
      toast.success(`Marked as ${newValue}`);
    } catch (error) {
      console.warn(
        "Backend quick update failed. Local state updated successfully.",
      );
      toast.success(`Marked as ${newValue}`);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedLeads(leads.map((lead) => lead._id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId) => {
    if (selectedLeads.includes(leadId)) {
      setSelectedLeads(selectedLeads.filter((id) => id !== leadId));
    } else {
      setSelectedLeads([...selectedLeads, leadId]);
    }
  };

  const handleUpdateFollowUpAction = async (leadId, newAction) => {
    const score = FOLLOW_UP_ACTION[newAction] || 0;
    const leadCategory = getLeadCategory(score);
    const stage = getStageFromCategory(leadCategory);
    const stageStatus = STAGE_STATUS[stage] || "New";
    
    setLeads(prevLeads => prevLeads.map(lead => {
      if (lead._id === leadId) {
        return { 
          ...lead, 
          followUpAction: newAction,
          leadCategory: leadCategory,
          stage: stage,
          stageStatus: stageStatus
        };
      }
      return lead;
    }));
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";
      await axiosInstance.put(`${apiUrl}/leads/${leadId}`, {
        followUpAction: newAction,
        leadCategory: leadCategory,
        stage: stage,
        stageStatus: stageStatus
      });
      toast.success("Follow-up action updated");
    } catch (error) {
      console.error("Failed to update follow-up action", error);
      toast.error("Failed to update action");
      setRefreshTrigger(prev => prev + 1);
    }
  };

  const getTotalPages = () => {
    return Math.ceil(totalLeads / rowsPerPage);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= getTotalPages()) {
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const newRowsPerPage = parseInt(e.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const getDisplayRange = () => {
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalLeads);
    return { start, end };
  };

  if (authLoading) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#6B7280" }}>
        Loading permissions...
      </div>
    );
  }
  if (!hasCrmAccess || !canViewLeads) {
    return (
      <div
        style={{
          padding: "60px 40px",
          textAlign: "center",
          backgroundColor: "#fff",
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
          margin: "20px",
        }}
      >
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
        <h2 style={{ color: "#111827", marginBottom: "12px" }}>
          Access Denied
        </h2>
        <p
          style={{ color: "#6B7280", fontSize: "16px", marginBottom: "8px" }}
        >
          You don't have permission to access the Leads section.
        </p>
        <p style={{ color: "#9CA3AF", fontSize: "14px" }}>
          Please contact your administrator to request access.
        </p>
      </div>
    );
  }

  return (
    <div
      className="crm-leads-container"
      style={{
        padding: "12px",
        backgroundColor: "#f8f9fa",
        minHeight: "100%",
        fontFamily: "Inter, sans-serif",
        maxWidth: "100vw",
        overflowX: "hidden",
      }}
    >
      {/* Tabs Section - Mobile Responsive */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #E5E7EB",
          marginBottom: "16px",
          paddingBottom: "0",
          flexWrap: "wrap",
          gap: "4px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          {canViewOverview && (
            <Link to="/crm/overview" style={{ textDecoration: "none" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  color: "#9CA3AF",
                  fontSize: "12px",
                  fontWeight: "500",
                  paddingBottom: "10px",
                  borderBottom: "2px solid transparent",
                  cursor: "pointer",
                  padding: "6px 10px",
                }}
              >
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="3" y1="9" x2="21" y2="9"></line>
                  <line x1="9" y1="21" x2="9" y2="9"></line>
                </svg>
                <span style={{ display: "inline" }}>Overview</span>
              </div>
            </Link>
          )}
          <div
            className="nav-item active"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "4px 12px",
              borderRadius: "999px",
              fontSize: "12px",
              fontWeight: "600",
              cursor: "pointer",
              marginBottom: "6px",
              backgroundColor: "#8B5CF6",
              color: "#fff",
            }}
          >
            <div
              className="nav-dot"
              style={{
                width: "5px",
                height: "5px",
                borderRadius: "50%",
                backgroundColor: "currentColor",
              }}
            ></div>
            Leads
          </div>
          {canViewContacts && (
            <Link
              to="/crm/contacts"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                color: "#9CA3AF",
                fontSize: "12px",
                fontWeight: "500",
                paddingBottom: "10px",
                borderBottom: "2px solid transparent",
                cursor: "pointer",
                textDecoration: "none",
                padding: "6px 10px",
              }}
            >
              <svg
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              <span style={{ display: "inline" }}>Contacts</span>
            </Link>
          )}
          {canViewAccounts && (
            <Link to="/crm/accounts" style={{ textDecoration: "none" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  color: "#9CA3AF",
                  fontSize: "12px",
                  fontWeight: "500",
                  paddingBottom: "10px",
                  borderBottom: "2px solid transparent",
                  cursor: "pointer",
                  padding: "6px 10px",
                }}
              >
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                >
                  <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
                  <path d="M9 22v-4h6v4"></path>
                  <path d="M8 6h.01"></path>
                  <path d="M16 6h.01"></path>
                  <path d="M12 6h.01"></path>
                  <path d="M12 10h.01"></path>
                  <path d="M12 14h.01"></path>
                  <path d="M16 10h.01"></path>
                  <path d="M16 14h.01"></path>
                  <path d="M8 10h.01"></path>
                  <path d="M8 14h.01"></path>
                </svg>
                <span style={{ display: "inline" }}>Accounts</span>
              </div>
            </Link>
          )}
          {canViewTasks && (
            <Link to="/crm/tasks" style={{ textDecoration: "none" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  color: "#9CA3AF",
                  fontSize: "12px",
                  fontWeight: "500",
                  paddingBottom: "10px",
                  borderBottom: "2px solid transparent",
                  cursor: "pointer",
                  padding: "6px 10px",
                }}
              >
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                >
                  <polyline points="9 11 12 14 22 4"></polyline>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                </svg>
                <span style={{ display: "inline" }}>Tasks</span>
              </div>
            </Link>
          )}
          {canViewCampaigns && (
            <Link to="/crm/campaigns" style={{ textDecoration: "none" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  color: "#9CA3AF",
                  fontSize: "12px",
                  fontWeight: "500",
                  paddingBottom: "10px",
                  borderBottom: "2px solid transparent",
                  cursor: "pointer",
                  padding: "6px 10px",
                }}
              >
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                </svg>
                <span style={{ display: "inline" }}>Campaigns</span>
              </div>
            </Link>
          )}
        </div>
      </div>

      {/* Header - Mobile Responsive */}
      <div
        className="crm-leads-header"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        <div>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              margin: "0 0 2px 0",
              color: "#111827",
            }}
          >
            Leads
          </h2>
          <p style={{ margin: 0, color: "#6B7280", fontSize: "12px" }}>
            Manage and track all leads generated from your campaigns
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            onClick={() => setShowExportModal(true)}
            style={{
              padding: "6px 12px",
              backgroundColor: "#fff",
              border: "1px solid #E5E7EB",
              borderRadius: "6px",
              fontWeight: "600",
              color: "#374151",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "11px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              flex: "1 1 auto",
              justifyContent: "center",
            }}
          >
            <svg
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            Export
          </button>
          {canCreateLead && (
            <>
              <button
                onClick={() => setShowImportModal(true)}
                style={{
                  padding: "6px 12px",
                  backgroundColor: "#fff",
                  border: "1px solid #7C3AED",
                  borderRadius: "6px",
                  fontWeight: "600",
                  color: "#7C3AED",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "11px",
                  boxShadow: "0 1px 2px rgba(124,58,237,0.1)",
                  flex: "1 1 auto",
                  justifyContent: "center",
                }}
              >
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Import
              </button>
              <button
                onClick={() => setShowAddLeadModal(true)}
                style={{
                  padding: "6px 12px",
                  backgroundColor: "#7C3AED",
                  border: "none",
                  borderRadius: "6px",
                  fontWeight: "600",
                  color: "#fff",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "11px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  flex: "1 1 auto",
                  justifyContent: "center",
                }}
              >
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                >
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add Lead
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats Row - Mobile Responsive Grid */}
      <div
        className="crm-stats-row"
        style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", 
          gap: "8px", 
          marginBottom: "12px",
        }}
      >
        {/* Total Leads */}
        <div
          onClick={() => setSelectedWidget("total")}
          style={{
            backgroundColor: selectedWidget === "total" ? "#F5F3FF" : "#fff",
            padding: "8px 12px",
            borderRadius: "8px",
            border: selectedWidget === "total" ? "2px solid #7C3AED" : "1px solid #F3F4F6",
            boxShadow: selectedWidget === "total" ? "0 2px 8px rgba(124, 58, 237, 0.12)" : "0 2px 4px -1px rgba(0,0,0,0.05)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <div>
            <div style={{ color: "#6B7280", fontSize: "10px", fontWeight: "500" }}>
              Total
            </div>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "#111827", marginTop: "2px" }}>
              {computedStats.totalLeads || 0}
            </div>
          </div>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              backgroundColor: "#E0F2FE",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#0284C7"
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
        </div>

        {/* Qualified Leads */}
        <div
          onClick={() => setSelectedWidget((prev) => (prev === "qualified" ? "total" : "qualified"))}
          style={{
            backgroundColor: selectedWidget === "qualified" ? "#ECFDF5" : "#fff",
            padding: "8px 12px",
            borderRadius: "8px",
            border: selectedWidget === "qualified" ? "2px solid #10B981" : "1px solid #F3F4F6",
            boxShadow: selectedWidget === "qualified" ? "0 2px 8px rgba(16, 185, 129, 0.12)" : "0 2px 4px -1px rgba(0,0,0,0.05)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <div>
            <div style={{ color: "#6B7280", fontSize: "10px", fontWeight: "500" }}>
              Qualified
            </div>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "#111827", marginTop: "2px" }}>
              {computedStats.qualifiedLeads || 0}
            </div>
          </div>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              backgroundColor: "#D1FAE5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#059669"
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
        </div>

        {/* Proposals Shared */}
        <div
          onClick={() => setSelectedWidget((prev) => (prev === "proposal" ? "total" : "proposal"))}
          style={{
            backgroundColor: selectedWidget === "proposal" ? "#FFFBEB" : "#fff",
            padding: "8px 12px",
            borderRadius: "8px",
            border: selectedWidget === "proposal" ? "2px solid #F59E0B" : "1px solid #F3F4F6",
            boxShadow: selectedWidget === "proposal" ? "0 2px 8px rgba(245, 158, 11, 0.12)" : "0 2px 4px -1px rgba(0,0,0,0.05)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <div>
            <div style={{ color: "#6B7280", fontSize: "10px", fontWeight: "500" }}>
              Proposals
            </div>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "#111827", marginTop: "2px" }}>
              {computedStats.proposalsShared || 0}
            </div>
          </div>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              backgroundColor: "#FEF3C7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#D97706"
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
        </div>

        {/* Negotiations */}
        <div
          onClick={() => setSelectedWidget((prev) => (prev === "negotiation" ? "total" : "negotiation"))}
          style={{
            backgroundColor: selectedWidget === "negotiation" ? "#F5F3FF" : "#fff",
            padding: "8px 12px",
            borderRadius: "8px",
            border: selectedWidget === "negotiation" ? "2px solid #8B5CF6" : "1px solid #F3F4F6",
            boxShadow: selectedWidget === "negotiation" ? "0 2px 8px rgba(139, 92, 246, 0.12)" : "0 2px 4px -1px rgba(0,0,0,0.05)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <div>
            <div style={{ color: "#6B7280", fontSize: "10px", fontWeight: "500" }}>
              Negotiations
            </div>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "#111827", marginTop: "2px" }}>
              {computedStats.negotiations || 0}
            </div>
          </div>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              backgroundColor: "#F3E8FF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#9333EA"
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 6v6l4 2"></path>
            </svg>
          </div>
        </div>

        {/* Won Leads */}
        <div
          onClick={() => setSelectedWidget((prev) => (prev === "won" ? "total" : "won"))}
          style={{
            backgroundColor: selectedWidget === "won" ? "#ECFDF5" : "#fff",
            padding: "8px 12px",
            borderRadius: "8px",
            border: selectedWidget === "won" ? "2px solid #10B981" : "1px solid #F3F4F6",
            boxShadow: selectedWidget === "won" ? "0 2px 8px rgba(16, 185, 129, 0.12)" : "0 2px 4px -1px rgba(0,0,0,0.05)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <div>
            <div style={{ color: "#6B7280", fontSize: "10px", fontWeight: "500" }}>
              Won
            </div>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "#111827", marginTop: "2px" }}>
              {computedStats.wonLeads || 0}
            </div>
          </div>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              backgroundColor: "#D1FAE5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#10B981"
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              <polyline points="9 12 11 14 15 10"></polyline>
            </svg>
          </div>
        </div>

        {/* Lost Leads */}
        <div
          onClick={() => setSelectedWidget((prev) => (prev === "lost" ? "total" : "lost"))}
          style={{
            backgroundColor: selectedWidget === "lost" ? "#FEF2F2" : "#fff",
            padding: "8px 12px",
            borderRadius: "8px",
            border: selectedWidget === "lost" ? "2px solid #EF4444" : "1px solid #F3F4F6",
            boxShadow: selectedWidget === "lost" ? "0 2px 8px rgba(239, 68, 68, 0.12)" : "0 2px 4px -1px rgba(0,0,0,0.05)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <div>
            <div style={{ color: "#6B7280", fontSize: "10px", fontWeight: "500" }}>
              Lost
            </div>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "#111827", marginTop: "2px" }}>
              {computedStats.lostLeads || 0}
            </div>
          </div>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              backgroundColor: "#FEE2E2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#EF4444"
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
        </div>
      </div>

      {/* Stats Row 2 - Additional Stats */}
      <div
        className="crm-stats-row-2"
        style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", 
          gap: "8px", 
          marginBottom: "12px",
        }}
      >
        {/* Win Rate */}
        <div
          style={{
            backgroundColor: "#fff",
            padding: "6px 10px",
            borderRadius: "8px",
            border: "1px solid #F3F4F6",
            boxShadow: "0 2px 4px -1px rgba(0,0,0,0.05)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <div>
            <div style={{ color: "#6B7280", fontSize: "9px", fontWeight: "500" }}>
              Win Rate
            </div>
            <div style={{ fontSize: "16px", fontWeight: "700", color: "#111827", marginTop: "1px" }}>
              {stats?.winRate || 0}%
            </div>
          </div>
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              backgroundColor: "#E0E7FF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#4F46E5"
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
          </div>
        </div>

        {/* Avg. Lead Age */}
        <div
          style={{
            backgroundColor: "#fff",
            padding: "6px 10px",
            borderRadius: "8px",
            border: "1px solid #F3F4F6",
            boxShadow: "0 2px 4px -1px rgba(0,0,0,0.05)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <div>
            <div style={{ color: "#6B7280", fontSize: "9px", fontWeight: "500" }}>
              Avg. Age
            </div>
            <div style={{ fontSize: "16px", fontWeight: "700", color: "#111827", marginTop: "1px" }}>
              {stats.avgLeadAge || 0}d
            </div>
          </div>
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              backgroundColor: "#FCE7F3",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#DB2777"
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
        </div>

        {/* Avg. Follow-up Count */}
        <div
          style={{
            backgroundColor: "#fff",
            padding: "6px 10px",
            borderRadius: "8px",
            border: "1px solid #F3F4F6",
            boxShadow: "0 2px 4px -1px rgba(0,0,0,0.05)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <div>
            <div style={{ color: "#6B7280", fontSize: "9px", fontWeight: "500" }}>
              Avg. Follow-up
            </div>
            <div style={{ fontSize: "16px", fontWeight: "700", color: "#111827", marginTop: "1px" }}>
              {stats.avgFollowUpCount || 0}
            </div>
          </div>
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              backgroundColor: "#E0F2FE",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#0284C7"
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
            </svg>
          </div>
        </div>
      </div>

      {/* Mobile Filter Button */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
        <button
          onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
          style={{
            padding: "6px 12px",
            backgroundColor: isMobileFilterOpen ? "#7C3AED" : "#fff",
            border: isMobileFilterOpen ? "none" : "1px solid #E5E7EB",
            borderRadius: "6px",
            color: isMobileFilterOpen ? "#fff" : "#374151",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
          </svg>
          Filters {isMobileFilterOpen ? "▲" : "▼"}
        </button>
        
        {Object.values(visibleColumns).filter(v => v).length > 0 && (
          <button
            onClick={() => setShowColumnToggle(!showColumnToggle)}
            style={{
              padding: "6px 12px",
              backgroundColor: showColumnToggle ? "#7C3AED" : "#fff",
              border: showColumnToggle ? "none" : "1px solid #E5E7EB",
              borderRadius: "6px",
              color: showColumnToggle ? "#fff" : "#374151",
              fontSize: "12px",
              fontWeight: "500",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="9" y1="3" x2="9" y2="21"></line>
            </svg>
            Columns
          </button>
        )}
        
        <button
          onClick={() => {
            setSearchTerm("");
            setSelectedStatus("");
            setSelectedStage("");
            setSelectedWidget("total");
            setRefreshTrigger((prev) => prev + 1);
            setIsRefreshing(true);
            setTimeout(() => setIsRefreshing(false), 500);
          }}
          style={{
            padding: "6px 10px",
            backgroundColor: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: "6px",
            color: "#6B7280",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "12px",
          }}
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <polyline points="23 4 23 10 17 10"></polyline>
            <polyline points="1 20 1 14 7 14"></polyline>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
          </svg>
          Reset
        </button>
      </div>

      {/* Mobile Filters - Expandable */}
      {isMobileFilterOpen && (
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "8px",
            padding: "12px",
            marginBottom: "12px",
            border: "1px solid #E5E7EB",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <div style={{ position: "relative", width: "100%" }}>
            <span
              style={{
                position: "absolute",
                left: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9CA3AF",
              }}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search leads..."
              style={{
                width: "100%",
                padding: "8px 10px 8px 32px",
                border: "1px solid #E5E7EB",
                borderRadius: "6px",
                outline: "none",
                fontSize: "12px",
                color: "#111827",
                boxSizing: "border-box",
              }}
            />
          </div>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{
              padding: "8px 10px",
              border: "1px solid #E5E7EB",
              borderRadius: "6px",
              outline: "none",
              fontSize: "12px",
              backgroundColor: "#fff",
              color: "#374151",
              cursor: "pointer",
              width: "100%",
            }}
          >
            <option value="">All Status</option>
            <option value="New">New</option>
            <option value="Contracted">Contracted</option>
            <option value="Interested">Interested</option>
            <option value="Qualified">Qualified</option>
            <option value="Lost">Lost</option>
            <option value="Converted">Converted</option>
            <option value="Others">Others</option>
          </select>

          <select
            value={selectedStage}
            onChange={(e) => setSelectedStage(e.target.value)}
            style={{
              padding: "8px 10px",
              border: "1px solid #E5E7EB",
              borderRadius: "6px",
              outline: "none",
              fontSize: "12px",
              backgroundColor: "#fff",
              color: "#374151",
              cursor: "pointer",
              width: "100%",
            }}
          >
            <option value="">All Stages</option>
            <option value="New Lead">New Lead</option>
            <option value="Qualified">Qualified</option>
            <option value="Proposal">Proposal</option>
            <option value="Negotiation">Negotiation</option>
            <option value="Won">Won</option>
            <option value="Lost">Lost</option>
            <option value="Closed">Closed</option>
          </select>
        </div>
      )}

      {/* Table Container */}
      <div
        className="crm-table-container"
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          border: "1px solid #F3F4F6",
          boxShadow: "0 2px 4px -1px rgba(0,0,0,0.05)",
          overflow: "hidden",
        }}
      >
        {/* Table - Mobile Responsive with horizontal scroll */}
        <div
          style={{
            overflowX: "auto",
            overflowY: "visible",
            WebkitOverflowScrolling: "touch",
            paddingBottom: "4px",
            maxWidth: "100%",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              textAlign: "left",
              minWidth: viewMode === "kanban" ? "100%" : "800px",
              fontSize: "12px",
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: "#F9FAFB",
                  borderBottom: "1px solid #F3F4F6",
                }}
              >
                <th
                  style={{
                    padding: "8px 10px",
                    fontWeight: "600",
                    fontSize: "10px",
                    color: "#6B7280",
                    textTransform: "uppercase",
                    width: "30px",
                  }}
                >
<input
                    type="checkbox"
                    checked={
                      displayedLeads.length > 0 &&
                      selectedLeads.length === displayedLeads.length
                    }
                    onChange={handleSelectAll}
                    style={{
                      width: "14px",
                      height: "14px",
                      borderRadius: "4px",
                      border: "1px solid #D1D5DB",
                      cursor: "pointer",
                    }}
                  />
                </th>
                {(() => {
                  const activeOrderedCols = Array.from(new Set([
                    ...columnOrder,
                    ...Object.keys(defaultColumnNames),
                    ...Object.keys(visibleColumns),
                    ...customFields.map(f => `custom_${f.fieldKey}`)
                  ])).filter(colKey => visibleColumns[colKey] !== false);

                  // For mobile, show fewer columns by default
                  const mobileCols = activeOrderedCols.filter(colKey => 
                    ['leadId', 'lead', 'phone', 'status', 'action'].includes(colKey) || 
                    colKey.startsWith('custom_')
                  );
                  
                  const colsToRender = window.innerWidth < 768 ? mobileCols : activeOrderedCols;

                  return colsToRender.map((colKey) => {
                    const isCustomField = colKey.startsWith('custom_');
                    const fieldKey = isCustomField ? colKey.replace('custom_', '') : null;
                    const customField = customFields.find((f) => f.fieldKey === fieldKey);
                    const label =
                      columnNames[colKey]
                        ? columnNames[colKey].toUpperCase()
                        : defaultColumnNames[colKey]
                        ? defaultColumnNames[colKey].toUpperCase()
                        : isCustomField
                        ? (customField?.fieldName || fieldKey).replace(/_/g, ' ').toUpperCase()
                        : colKey.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim().toUpperCase();

                    return (
                      <th
                        key={colKey}
                        style={{
                          padding: "8px 10px",
                          fontWeight: "600",
                          fontSize: "10px",
                          color: "#6B7280",
                          textTransform: "uppercase",
                          textAlign: colKey === 'action' ? 'center' : 'left',
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </th>
                    );
                  });
                })()}
              </tr>
            </thead>
            <tbody>
              {isLoadingData ? (
                <tr>
                  <td
                    colSpan={
                      Object.values(visibleColumns).filter(Boolean).length + 1
                    }
                    style={{
                      padding: "30px",
                      textAlign: "center",
                      color: "#6B7280",
                      fontSize: "13px",
                      fontWeight: "500",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                      }}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ animation: "spin 1s linear infinite" }}
                      >
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        <line x1="12" y1="2" x2="12" y2="6"></line>
                        <line x1="12" y1="18" x2="12" y2="22"></line>
                        <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                        <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                        <line x1="2" y1="12" x2="6" y2="12"></line>
                        <line x1="18" y1="12" x2="22" y2="12"></line>
                        <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                        <line x1="16.24" y1="4.93" x2="19.07" y2="7.76"></line>
                      </svg>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : displayedLeads.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      Object.values(visibleColumns).filter(Boolean).length + 1
                    }
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#6B7280",
                      fontSize: "12px",
                    }}
                  >
                    No leads found.
                  </td>
                </tr>
              ) : (
                displayedLeads.map((lead) => {
                  const followUpAction = lead.followUpAction || "Call Customer";
                  const score = FOLLOW_UP_ACTION[followUpAction] || 0;
                  const leadCategory = getLeadCategory(score);
                  const stage = lead.stage || (lead.leadStatus === 'Lost' ? 'Lost' : (lead.leadStatus === 'Won' ? 'Won' : getStageFromCategory(leadCategory)));
                  const stageStatus = lead.stageStatus || STAGE_STATUS[stage] || "New";
                  let assignedTo="-"
                  try {
                    const assignedUserName=allusers?.find((val)=>val._id==lead?.assignedTo)?.profile?.name|| "-"
                    assignedTo = assignedUserName || "-";
                  } catch (error) {}
                  let followUpCount = 0;
                  try {
                    followUpCount = lead?.followUpCount || 0;
                  } catch(e) {}

                  const name =
                    lead.customFields?.full_name ||
                    lead.formData?.full_name ||
                    lead.formData?.Full_Name ||
                    lead.formData?.name ||
                    lead.name ||
                    lead.userId?.name ||
                    "Unknown";
                  const initial = name.charAt(0).toUpperCase();

                  const email =
                    lead.formData?.email ||
                    lead.customFields?.email ||
                    lead.email ||
                    lead.userId?.email ||
                    "-";

                  const rawPhone =
                    lead.formData?.phone ||
                    lead.formData?.contact ||
                    lead.customFields?.phone ||
                    lead.userId?.mobile ||
                    "";
                  const phone = rawPhone ? String(rawPhone).replace(/^p:/i, '').trim() : "-";
                  const status = lead.leadStatus || "New";

                  let statusColor = "#F3F4F6";
                  let statusText = "#374151";
                  if (status === "New") {
                    statusColor = "#D1FAE5";
                    statusText = "#059669";
                  } else if (status === "Contacted") {
                    statusColor = "#DBEAFE";
                    statusText = "#1D4ED8";
                  } else if (status === "Interested") {
                    statusColor = "#FEF3C7";
                    statusText = "#D97706";
                  }

                  let remarksSnippet = "-";
                  let fullremarksSnippet = "-";

                  const noteText = (Array.isArray(lead.notes) && lead.notes.length > 0)
                    ? (lead.notes[lead.notes.length - 1]?.text || lead.notes[lead.notes.length - 1])
                    : (lead.remarks || lead.formData?.remarks || lead.formData?.Remarks || lead.formData?.Feedback || lead.formData?.feedback || "");

                  if (noteText && typeof noteText === 'string') {
                    fullremarksSnippet = noteText;
                    remarksSnippet = noteText.length > 30 ? noteText.substring(0, 30) + "..." : noteText;
                  }

                  const activeOrderedCols = Array.from(new Set([
                    ...columnOrder,
                    ...Object.keys(defaultColumnNames),
                    ...Object.keys(visibleColumns),
                    ...customFields.map(f => `custom_${f.fieldKey}`)
                  ])).filter(colKey => visibleColumns[colKey] !== false);

                  const mobileCols = activeOrderedCols.filter(colKey => 
                    ['leadId', 'lead', 'phone', 'status', 'action'].includes(colKey) || 
                    colKey.startsWith('custom_')
                  );
                  
                  const colsToRender = window.innerWidth < 768 ? mobileCols : activeOrderedCols;

                  return (
                    <tr
                      key={lead._id}
                      style={{
                        borderBottom: "1px solid #F3F4F6",
                        transition: "background-color 0.2s",
                        backgroundColor: selectedLeads.includes(lead._id)
                          ? "#F3F4F6"
                          : "transparent",
                      }}
                    >
                      <td style={{ padding: "8px 10px" }}>
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead._id)}
                          onChange={() => handleSelectLead(lead._id)}
                          style={{
                            width: "14px",
                            height: "14px",
                            borderRadius: "4px",
                            border: "1px solid #D1D5DB",
                            cursor: "pointer",
                          }}
                        />
                      </td>
                      {colsToRender.map((colKey) => {
                        if (colKey === "leadId") {
                          return (
                            <td key={colKey} style={{ padding: "8px 10px" }}>
                              <div
                                onClick={() => {
                                  setOpenDropdownId(null);
                                  let notesStr = "";
                                  let leadNotes = [];
                                  try {
                                    const savedNotes = JSON.parse(
                                      localStorage.getItem("crm_lead_notes") || "{}"
                                    );
                                    leadNotes = savedNotes[lead._id] || [];
                                    notesStr = leadNotes.map((n) => n.text).join("\n");
                                  } catch (e) {}
                                  const resolvedLeadName =
                                    lead.customFields?.full_name ||
                                    lead.formData?.full_name ||
                                    lead.formData?.Full_Name ||
                                    (lead.formData?.name && lead.formData?.name !== 'AD 2' ? lead.formData?.name : null) ||
                                    lead.name ||
                                    lead.userId?.name ||
                                    "";

                                  setEditLeadData({
                                    _id: lead._id,
                                    name: resolvedLeadName,
                                    email: lead.formData?.email || lead.customFields?.email || lead.email || "",
                                    phone: lead.formData?.phone || lead.customFields?.phone || lead.phone || "",
                                    source: lead.source || "",
                                    sourceId: lead.sourceId || "",
                                    address: lead.formData?.address || "",
                                    organization: lead.formData?.organization || "",
                                    leadStatus: lead.leadStatus || "New",
                                    paymentStatus: lead.paymentStatus || "Pending",
                                    notes: Array.isArray(lead.notes) && lead.notes.length > 0 ? lead.notes : leadNotes,
                                    remarks: lead.remarks || lead.formData?.remarks || lead.formData?.Remarks || lead.formData?.Feedback || "",
                                    assignedTo: lead.assignedTo || "",
                                    createdAt: lead.createdAt || lead.createdTime || "",
                                    createdBy: lead.createdBy || "",
                                    createdOn: lead.createdOn || lead.createdAt || "",
                                    updatedBy: lead.updatedBy || "",
                                    updatedOn: lead.updatedOn || lead.updatedAt || "",
                                    expectedStartDate: lead?.expectedStartDate || '',
                                    probability: lead?.probability || 0,
                                    followUpAction: lead?.followUpAction || '',
                                    nextAction: lead?.nextAction || '',
                                    nextActionDueDate: lead?.nextActionDueDate || '',
                                    customFields: lead.customFields || {},
                                    createdTime: lead?.createdTime
                                  });
                                  setShowEditLeadModal(true);
                                }}
                                style={{
                                  fontWeight: "600",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                  color: "#355089",
                                  fontSize: "11px",
                                }}
                              >
                                {lead._id ? lead._id.slice(-6).toUpperCase() : "-"}
                              </div>
                            </td>
                          );
                        }

                        if (colKey === "lead" || colKey === "fullName") {
                          return (
                            <td key={colKey} style={{ padding: "8px 10px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <div
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                    borderRadius: "50%",
                                    backgroundColor: "#E0F2FE",
                                    color: "#0284C7",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "bold",
                                    fontSize: "11px",
                                    flexShrink: 0,
                                  }}
                                >
                                  {initial}
                                </div>
                                <span
                                  style={{
                                    fontWeight: "600",
                                    color: "#111827",
                                    fontSize: "11px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {name}
                                </span>
                              </div>
                            </td>
                          );
                        }

                        if (colKey === "phone") {
                          return (
                            <td key={colKey} style={{ padding: "8px 10px" }}>
                              <div style={{ color: "#6B7280", fontSize: "11px", whiteSpace: "nowrap" }}>
                                {phone}
                              </div>
                            </td>
                          );
                        }

                        if (colKey === "status") {
                          return (
                            <td key={colKey} style={{ padding: "8px 10px" }}>
                              <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: "500", backgroundColor: statusColor, color: statusText, whiteSpace: "nowrap" }}>
                                {status}
                              </span>
                            </td>
                          );
                        }

                        if (colKey === "action") {
                          return (
                            <td key={colKey} style={{ padding: "8px 10px" }}>
                              <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                                <button
                                  onClick={() => setViewLeadData(lead)}
                                  style={{ width: "24px", height: "24px", borderRadius: "4px", border: "none", backgroundColor: "#F3F4F6", color: "#6B7280", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                  </svg>
                                </button>
                              </div>
                            </td>
                          );
                        }

                        // Custom Fields & Fallback
                        const fieldKey = colKey.startsWith("custom_") ? colKey.replace("custom_", "") : colKey;
                        const customField = customFields.find((f) => f.fieldKey === fieldKey);
                        const val = lead.customFields?.[fieldKey] ?? lead.formData?.[fieldKey] ?? lead[fieldKey] ?? "-";
                        return (
                          <td key={colKey} style={{ padding: "8px 10px" }}>
                            <div style={{ color: "#6B7280", fontSize: "11px", whiteSpace: "nowrap" }}>
                              {customField && customField.fieldType === "checkbox" ? (val === true || val === "true" ? "✓" : "✗") : String(val !== null && val !== undefined && val !== "" ? val : "-")}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination - Mobile Responsive */}
        <div
          style={{
            padding: "10px 12px",
            borderTop: "1px solid #F3F4F6",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: "11px", color: "#6B7280" }}>
            Showing {totalLeads > 0 ? getDisplayRange().start : 0} to {getDisplayRange().end} of {totalLeads} leads
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", justifyContent: "center" }}>
            <select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              style={{
                padding: "4px 24px 4px 8px",
                border: "1px solid #E5E7EB",
                borderRadius: "4px",
                outline: "none",
                fontSize: "11px",
                backgroundColor: "#fff",
                color: "#374151",
                cursor: "pointer",
                appearance: "none",
                backgroundImage:
                  'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20fill%3D%22none%22%20stroke%3D%22%236B7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 6px center",
                backgroundSize: "10px",
              }}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
            
            <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  width: "24px",
                  height: "24px",
                  border: "none",
                  backgroundColor: "transparent",
                  color: currentPage === 1 ? "#D1D5DB" : "#9CA3AF",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  opacity: currentPage === 1 ? 0.5 : 1,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              
              {Array.from({ length: Math.min(3, getTotalPages()) }, (_, i) => {
                let pageNumber;
                const totalPages = getTotalPages();
                
                if (totalPages <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage <= 2) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 1) {
                  pageNumber = totalPages - 2 + i;
                } else {
                  pageNumber = currentPage - 1 + i;
                }
                
                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    style={{
                      width: "24px",
                      height: "24px",
                      border: "none",
                      backgroundColor: currentPage === pageNumber ? "#3B82F6" : "transparent",
                      color: currentPage === pageNumber ? "#fff" : "#374151",
                      borderRadius: "4px",
                      fontSize: "11px",
                      cursor: "pointer",
                      fontWeight: currentPage === pageNumber ? "600" : "500",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {pageNumber}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === getTotalPages() || getTotalPages() === 0}
                style={{
                  width: "24px",
                  height: "24px",
                  border: "none",
                  backgroundColor: "transparent",
                  color: currentPage === getTotalPages() || getTotalPages() === 0 ? "#D1D5DB" : "#9CA3AF",
                  cursor: currentPage === getTotalPages() || getTotalPages() === 0 ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  opacity: currentPage === getTotalPages() || getTotalPages() === 0 ? 0.5 : 1,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* All Modal Components - Same as original but with responsive adjustments */}
      {/* ... (all modals remain the same, but you may want to adjust their max-width for mobile) */}

      <style>{`
        @media (max-width: 768px) {
          .crm-leads-container {
            padding: 8px !important;
          }
          .crm-stats-row {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 6px !important;
          }
          .crm-stats-row-2 {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 6px !important;
          }
        }
        @media (max-width: 480px) {
          .crm-stats-row {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .crm-stats-row-2 {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>

      {/* Floating Action Buttons - Positioned for mobile */}
      <div style={{
        position: "fixed",
        bottom: "20px",
        right: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        zIndex: 99,
      }}>
        {/* Column Toggle Button */}
        <button
          onClick={() => setShowColumnToggle(!showColumnToggle)}
          style={{
            width: "40px",
            height: "40px",
            backgroundColor: "#6366F1",
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: "0 2px 8px rgba(99, 102, 241, 0.3)",
            border: "none",
            cursor: "pointer",
            color: "#fff",
          }}
        >
          {showColumnToggle ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="9" y1="3" x2="9" y2="21"></line>
            </svg>
          )}
        </button>

        {/* View Toggle Button */}
        <button
          onClick={() => setShowFloatingView(!showFloatingView)}
          style={{
            width: "40px",
            height: "40px",
            backgroundColor: "#8B5CF6",
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: "0 2px 8px rgba(139, 92, 246, 0.3)",
            border: "none",
            cursor: "pointer",
            color: "#fff",
          }}
        >
          {showFloatingView ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
          )}
        </button>
      </div>

      {/* Floating View Toggle Bar - Mobile */}
      {showFloatingView && (
        <div
          style={{
            position: "fixed",
            bottom: "110px",
            right: "20px",
            backgroundColor: "#fff",
            borderRadius: "8px",
            padding: "6px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            border: "1px solid #E5E7EB",
            zIndex: 98,
          }}
        >
          <button
            onClick={() => setViewMode("kanban")}
            style={{
              padding: "6px 12px",
              backgroundColor: viewMode === "kanban" ? "#8B5CF6" : "transparent",
              border: "none",
              borderRadius: "4px",
              color: viewMode === "kanban" ? "#fff" : "#6B7280",
              fontSize: "11px",
              fontWeight: viewMode === "kanban" ? "600" : "500",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Kanban
          </button>
          <button
            onClick={() => setViewMode("table")}
            style={{
              padding: "6px 12px",
              backgroundColor: viewMode === "table" ? "#8B5CF6" : "transparent",
              border: "none",
              borderRadius: "4px",
              color: viewMode === "table" ? "#fff" : "#6B7280",
              fontSize: "11px",
              fontWeight: viewMode === "table" ? "600" : "500",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Table
          </button>
        </div>
      )}

      {/* Column Toggle - Mobile */}
      {showColumnToggle && (
        <div
          style={{
            position: "fixed",
            bottom: "160px",
            right: "20px",
            backgroundColor: "#fff",
            borderRadius: "8px",
            padding: "12px",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            border: "1px solid #E5E7EB",
            zIndex: 98,
            width: "200px",
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          {/* <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
            <span style={{ fontSize: "11px", fontWeight: "600", color: "#374151" }}>Columns</span>
            <button
              onClick={() => {
                const allKeys = Object.keys(visibleColumns);
                const allTrue = allKeys.every(k => visibleColumns[k]);
                const newState = {};
                allKeys.forEach(k => { newState[k] = !allTrue; });
                setVisibleColumns(newState);
              }}
              style={{
                fontSize: "10px",
                color: "#7C3AED",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              {Object.values(visibleColumns).every(v => v) ? "Hide All" : "Show All"}
            </button>
          </div> */}
          {/* {Object.keys(visibleColumns).map((col) => (
            <label key={col} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "#4B5563" }}>
              <input
                type="checkbox"
                checked={visibleColumns[col]}
                onChange={() => setVisibleColumns(prev => ({ ...prev, [col]: !prev[col] }))}
                style={{ width: "14px", height: "14px", cursor: "pointer" }}
              />
              {columnNames[col] || col.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
            </label>
          ))} */}
        </div>
      )}

      {/* Import Modal - responsive */}
      <ImportLeadsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        tenantId={access?.tenantId}
        customFields={customFields}
        columnNames={columnNames}
        onImportSuccess={(newLeads) => {
          if (newLeads && newLeads.length > 0) {
            setLeads((prev) => [...newLeads, ...prev]);
          }
          setColumnNamesRefresh(prev => !prev);
          setRefreshTrigger((prev) => prev + 1);
        }}
      />
      
      <CustomColumnManager
        isOpen={showCustomColumnManager}
        onClose={() => setShowCustomColumnManager(false)}
        tenantId={access?.tenantId}
        visibleColumns={visibleColumns}
        columnNames={columnNames}
        setVisibleColumns={setVisibleColumns}
        setColumnNames={setColumnNames}
        onColumnsUpdate={() => {
          setColumnNamesRefresh(!columnRefresh)
          setRefreshTrigger(prev => prev + 1);
        }}
      />

      {/* Edit Lead Modal */}
      {showEditLeadModal && editLeadData && (
        <EditLeadModal
          canEditLead={canEditLead}
          customFields={customFields}
          isOpen={showEditLeadModal}
          leadData={editLeadData}
          onClose={() => setShowEditLeadModal(false)}
          onUpdate={async (payload) => {
            setIsSavingEdit(true);
            try {
              const axiosInstance = createAxiosInstance();
              const apiUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";

              const response = await axiosInstance.put(
                `${apiUrl}/leads/${payload._id}`,
                {
                  formData: payload.formData,
                  leadStatus: payload.leadStatus,
                  paymentStatus: payload.paymentStatus,
                  amount: payload.amount,
                  assignedTo: payload.assignedTo,
                  followUpAction: payload.followUpAction,
                  leadCategory: payload.leadCategory,
                  stage: payload.stage,
                  stageStatus: payload.stageStatus,
                  changeReason: payload.changeReason,
                  assignedUserName: payload.assignedUserName,
                  expectedStartDate: payload.expectedStartDate,
                  probability: payload.probability,
                  nextActionDueDate: payload.nextActionDueDate,
                  nextAction: payload.nextAction,
                  notes: payload.notes,
                  customFields: payload.customFields
                }
              );

              if (response.data && response.data.success) {
                toast.success("Lead updated successfully!");
                setLeads((prevLeads) =>
                  prevLeads.map((lead) => {
                    if (lead._id === payload._id) {
                      return {
                        ...lead,
                        formData: payload.formData,
                        leadStatus: payload.leadStatus,
                        paymentStatus: payload.paymentStatus,
                        amount: payload.amount,
                        assignedTo: payload.assignedTo,
                        followUpAction: payload.followUpAction,
                        leadCategory: payload.leadCategory,
                        stage: payload.stage,
                        stageStatus: payload.stageStatus,
                        notes: payload.notes,
                        probability: payload.probability,
                        nextActionDueDate: payload.nextActionDueDate,
                        nextAction: payload.nextAction,
                        customFields: payload.customFields
                      };
                    }
                    return lead;
                  })
                );
                setShowEditLeadModal(false);
                setRefreshTrigger((prev) => prev + 1);
              } else {
                toast.error(response.data?.message || "Failed to update lead");
              }
            } catch (error) {
              console.error("Failed to update lead", error);
              toast.error(error?.response?.data?.message || "Failed to update lead");
            } finally {
              setIsSavingEdit(false);
            }
          }}
          allUsers={allusers}
          isSaving={isSavingEdit}
        />
      )}

      {/* Add Lead Modal */}
      {showAddLeadModal && (
        <AddLeadModal
          isOpen={showAddLeadModal}
          onClose={() => setShowAddLeadModal(false)}
          onSuccess={(newLead) => {
            setLeads(prevLeads => [newLead, ...prevLeads]);
            setTotalLeads(prev => prev + 1);
            setRefreshTrigger(prev => prev + 1);
            toast.success('Lead created successfully!');
          }}
          tenantId={access?.tenantId}
          campaignsList={campaignsList}
          customFields={customFields}
          allUsers={allusers}
          isLoading={isSavingLead}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "500px",
              padding: "20px",
              position: "relative",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#111827",
                }}
              >
                Export Leads
              </h3>
              <button
                onClick={() => setShowExportModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#6B7280",
                  fontSize: "18px",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  fontWeight: "600",
                  fontSize: "13px",
                  marginBottom: "10px",
                  color: "#374151",
                }}
              >
                Select Export Type:
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {["csv", "xlsx", "pdf"].map((type) => (
                  <label
                    key={type}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "13px",
                      color: "#6B7280",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="radio"
                      name="exportType"
                      checked={exportType === type}
                      onChange={() => setExportType(type)}
                      style={{
                        accentColor: "#3B82F6",
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                      }}
                    />
                    <span style={{ textTransform: "uppercase" }}>{type}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  fontWeight: "600",
                  fontSize: "13px",
                  marginBottom: "10px",
                  color: "#374151",
                }}
              >
                Filter Options:
              </div>
              <div style={{ marginBottom: "10px" }}>
                <div style={{ fontSize: "11px", color: "#9CA3AF", marginBottom: "4px" }}>
                  Campaign:
                </div>
                <select
                  value={exportCampaign}
                  onChange={(e) => setExportCampaign(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "6px",
                    outline: "none",
                    fontSize: "13px",
                    color: "#6B7280",
                    cursor: "pointer",
                    backgroundColor: "#fff",
                  }}
                >
                  <option value="">All Campaigns</option>
                  {campaignsList.map((camp) => (
                    <option key={camp._id} value={camp._id}>
                      {camp.title}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: "10px" }}>
                <div style={{ fontSize: "11px", color: "#9CA3AF", marginBottom: "4px" }}>
                  Status:
                </div>
                <select
                  value={exportStatus}
                  onChange={(e) => setExportStatus(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "6px",
                    outline: "none",
                    fontSize: "13px",
                    color: "#6B7280",
                    cursor: "pointer",
                    backgroundColor: "#fff",
                  }}
                >
                  <option value="">All Status</option>
                  <option value="New">New</option>
                  <option value="Contracted">Contracted</option>
                  <option value="Interested">Interested</option>
                  <option value="Qualified">Qualified</option>
                  <option value="Lost">Lost</option>
                  <option value="Converted">Converted</option>
                  <option value="Others">Others</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: "11px", color: "#9CA3AF", marginBottom: "4px" }}>
                  Payment:
                </div>
                <select
                  value={exportPayment}
                  onChange={(e) => setExportPayment(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "6px",
                    outline: "none",
                    fontSize: "13px",
                    color: "#6B7280",
                    cursor: "pointer",
                    backgroundColor: "#fff",
                  }}
                >
                  <option value="">All Payments</option>
                  <option value="Pending">Pending</option>
                  <option value="Successful">Successful</option>
                  <option value="Failed">Failed</option>
                </select>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                onClick={() => setShowExportModal(false)}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#F3F4F6",
                  border: "none",
                  borderRadius: "6px",
                  fontWeight: "600",
                  color: "#374151",
                  cursor: "pointer",
                  fontSize: "13px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={isExporting}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#7C3AED",
                  border: "none",
                  borderRadius: "6px",
                  fontWeight: "600",
                  color: "#fff",
                  cursor: isExporting ? "not-allowed" : "pointer",
                  fontSize: "13px",
                  opacity: isExporting ? 0.7 : 1,
                }}
              >
                {isExporting ? "Exporting..." : "Export"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Lead Modal */}
      {viewLeadData && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "450px",
              padding: "20px",
              position: "relative",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#111827",
                }}
              >
                Lead Details
              </h3>
              <button
                onClick={handleCloseViewLeadModal}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#6B7280",
                  fontSize: "20px",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                <span style={{ color: "#6B7280" }}>Lead ID:</span>
                <span style={{ color: "#111827", fontWeight: "500" }}>
                  {viewLeadData._id ? viewLeadData._id.slice(-6).toUpperCase() : "-"}
                </span>
              </div>
<div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                <span style={{ color: "#6B7280" }}>Name:</span>
                <span style={{ color: "#111827", fontWeight: "500" }}>
                  {viewLeadData.customFields?.full_name ||
                    viewLeadData.customFields?.name ||
                    viewLeadData.formData?.full_name ||
                    (viewLeadData.formData?.name && !viewLeadData.formData?.name.startsWith('AD ') && viewLeadData.formData?.name !== 'AD 2' ? viewLeadData.formData?.name : null) ||
                    (viewLeadData.name && !viewLeadData.name.startsWith('AD ') && viewLeadData.name !== 'AD 2' ? viewLeadData.name : null) ||
                    viewLeadData.userId?.name ||
                    "-"}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                <span style={{ color: "#6B7280" }}>Email:</span>
                <span style={{ color: "#111827", fontWeight: "500" }}>
                  {viewLeadData.formData?.email ||
                    viewLeadData.customFields?.email ||
                    viewLeadData.email ||
                    viewLeadData.userId?.email ||
                    "-"}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                <span style={{ color: "#6B7280" }}>Phone:</span>
                <span style={{ color: "#111827", fontWeight: "500" }}>
                  {viewLeadData.formData?.phone ||
                    viewLeadData.formData?.contact ||
                    viewLeadData.formData?.phone_number ||
                    viewLeadData.customFields?.phone_number ||
                    viewLeadData.customFields?.phone ||
                    viewLeadData.phone ||
                    viewLeadData.userId?.mobile ||
                    "-"}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                <span style={{ color: "#6B7280" }}>Campaign:</span>
                <span style={{ color: "#111827", fontWeight: "500" }}>
                  {viewLeadData.campaignId?.title ||
                    viewLeadData.customFields?.campaign_name ||
                    viewLeadData.campaign_name ||
                    viewLeadData.source ||
                    "-"}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                <span style={{ color: "#6B7280" }}>Payment:</span>
                <span style={{ color: "#111827", fontWeight: "500" }}>
                  {viewLeadData.paymentStatus || "Pending"}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                <span style={{ color: "#6B7280" }}>Status:</span>
                <span style={{ color: "#111827", fontWeight: "500" }}>
                  {viewLeadData.stage || viewLeadData.leadStatus || "New"}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                <span style={{ color: "#6B7280" }}>Submitted:</span>
                <span style={{ color: "#111827", fontWeight: "500" }}>
                  {viewLeadData.createdAt
                    ? new Date(viewLeadData.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "-"}
                </span>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={() => {
                  setContactLeadData(viewLeadData);
                  setViewLeadData(null);
                }}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#7C3AED",
                  border: "none",
                  borderRadius: "6px",
                  fontWeight: "600",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: "13px",
                }}
              >
                Contact
              </button>
              <button
                onClick={handleCloseViewLeadModal}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#F3F4F6",
                  border: "none",
                  borderRadius: "6px",
                  fontWeight: "600",
                  color: "#374151",
                  cursor: "pointer",
                  fontSize: "13px",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Lead Slide-in Panel */}
      {contactLeadData && (
        <>
          <style>{`
            @keyframes slideInRight {
              from { transform: translateX(100%); }
              to { transform: translateX(0); }
            }
            @keyframes slideOutRight {
              from { transform: translateX(0); }
              to { transform: translateX(100%); }
            }
            .contact-slide-in {
              animation: slideInRight 0.3s forwards;
            }
          `}</style>
          <div
            className="contact-slide-in"
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "100%",
              maxWidth: "400px",
              height: "100vh",
              backgroundColor: "#fff",
              zIndex: 1000,
              boxShadow: "-4px 0 20px rgba(0, 0, 0, 0.1)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid #E5E7EB",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <button
                onClick={() => {
                  setViewLeadData(contactLeadData);
                  setContactLeadData(null);
                }}
                style={{
                  background: "#fff",
                  border: "1px solid #E5E7EB",
                  borderRadius: "16px",
                  cursor: "pointer",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "12px",
                  fontWeight: "500",
                  padding: "4px 12px",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                Back
              </button>
              <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "600", color: "#111827" }}>
                Contact Lead
              </h3>
              <button
                onClick={() => setContactLeadData(null)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#6B7280",
                  fontSize: "20px",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: "20px", flex: 1, overflowY: "auto" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "24px" }}>
                <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#111827" }}>
                  {contactLeadData.formData?.name || contactLeadData.userId?.name || "-"}
                </h2>
                <span
                  style={{
                    padding: "2px 10px",
                    borderRadius: "9999px",
                    backgroundColor: "#DBEAFE",
                    color: "#2563EB",
                    fontSize: "10px",
                    fontWeight: "700",
                  }}
                >
                  {contactLeadData.leadStatus || "New"}
                </span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "24px" }}>
                <a
                  href={`https://wa.me/${(contactLeadData.formData?.phone || contactLeadData.formData?.contact || contactLeadData.userId?.mobile || "").replace(/[^0-9+]/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "10px 14px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    textDecoration: "none",
                    color: "#374151",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#D1FAE5",
                      color: "#10B981",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                    </svg>
                  </div>
                  <span style={{ fontSize: "13px", fontWeight: "500" }}>
                    {contactLeadData.formData?.phone || contactLeadData.formData?.contact || contactLeadData.userId?.mobile || "-"}
                  </span>
                  <svg style={{ marginLeft: "auto", color: "#9CA3AF" }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </a>

                <a
                  href={`tel:${(contactLeadData.formData?.phone || contactLeadData.formData?.contact || contactLeadData.userId?.mobile || "").replace(/[^0-9+]/g, "")}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "10px 14px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    textDecoration: "none",
                    color: "#374151",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#DCFCE7",
                      color: "#16A34A",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                  </div>
                  <span style={{ fontSize: "13px", fontWeight: "500" }}>
                    {contactLeadData.formData?.phone || contactLeadData.formData?.contact || contactLeadData.userId?.mobile || "-"}
                  </span>
                  <svg style={{ marginLeft: "auto", color: "#9CA3AF" }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </a>

                <a
                  href={`mailto:${contactLeadData.formData?.email || contactLeadData.userId?.email || ""}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "10px 14px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    textDecoration: "none",
                    color: "#374151",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#DBEAFE",
                      color: "#2563EB",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </div>
                  <span style={{ fontSize: "13px", fontWeight: "500" }}>
                    {contactLeadData.formData?.email || contactLeadData.userId?.email || "-"}
                  </span>
                  <svg style={{ marginLeft: "auto", color: "#9CA3AF" }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </a>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <div style={{ fontSize: "10px", fontWeight: "600", color: "#9CA3AF", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  NOTES
                </div>

                {notesList.map((note, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: "10px",
                      border: "1px dashed #D1D5DB",
                      borderRadius: "6px",
                      marginBottom: "10px",
                      backgroundColor: "#fff",
                    }}
                  >
                    <div style={{ fontSize: "12px", color: "#374151", marginBottom: "4px", lineHeight: "1.4" }}>
                      {renderFormattedNote(note.text)}
                    </div>
                    <div style={{ fontSize: "10px", color: "#9CA3AF" }}>
                      {note.date}
                    </div>
                  </div>
                ))}

                {isAddingNote ? (
                  <div
                    style={{
                      padding: "10px",
                      border: "1px dashed #7C3AED",
                      borderRadius: "6px",
                      backgroundColor: "#F5F3FF",
                      marginBottom: "12px",
                    }}
                  >
                    <textarea
                      autoFocus
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Type note here..."
                      style={{
                        width: "100%",
                        border: "none",
                        backgroundColor: "transparent",
                        outline: "none",
                        fontSize: "12px",
                        resize: "none",
                        minHeight: "50px",
                        color: "#374151",
                        fontFamily: "inherit",
                      }}
                    />
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "6px" }}>
                      <button
                        onClick={() => {
                          setIsAddingNote(false);
                          setNoteText("");
                        }}
                        style={{
                          padding: "4px 10px",
                          fontSize: "11px",
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                          color: "#6B7280",
                          fontWeight: "500",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveNote}
                        style={{
                          padding: "4px 10px",
                          fontSize: "11px",
                          backgroundColor: "#7C3AED",
                          color: "#fff",
                          borderRadius: "4px",
                          border: "none",
                          cursor: "pointer",
                          fontWeight: "500",
                        }}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsAddingNote(true)}
                    style={{
                      padding: "6px 14px",
                      border: "1px dashed #E5E7EB",
                      borderRadius: "6px",
                      backgroundColor: "#F9FAFB",
                      color: "#2563EB",
                      fontWeight: "600",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: "pointer",
                      width: "100%",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Add Note
                  </button>
                )}
              </div>

              <div>
                <div style={{ fontSize: "10px", fontWeight: "600", color: "#9CA3AF", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  ACTIVITY
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div style={{ width: "3px", backgroundColor: "#10B981", borderRadius: "2px" }}></div>
                    <div>
                      <div style={{ fontSize: "12px", fontWeight: "500", color: "#374151" }}>
                        Form Submitted{" "}
                        <span style={{ color: "#9CA3AF", fontSize: "11px", fontWeight: "400", marginLeft: "4px" }}>
                          {contactLeadData.createdAt ? new Date(contactLeadData.createdAt).toLocaleDateString("en-GB") : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div style={{ width: "3px", backgroundColor: "#F59E0B", borderRadius: "2px" }}></div>
                    <div>
                      <div style={{ fontSize: "12px", fontWeight: "500", color: "#374151" }}>
                        Payment: {contactLeadData.paymentStatus || "Pending"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Lead Modal */}
      {showDeleteLeadModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.55)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1050,
            padding: "16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              width: "100%",
              maxWidth: "420px",
              borderRadius: "12px",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.15)",
              textAlign: "center",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #F3F4F6",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#111827",
                }}
              >
                {deleteLeadId === "bulk"
                  ? `Delete ${selectedLeads.length} Leads`
                  : "Delete Lead"}
              </h3>
              <button
                onClick={() => setShowDeleteLeadModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  color: "#9CA3AF",
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: "16px 20px" }}>
              <p style={{ color: "#4B5563", fontSize: "13px", margin: 0, lineHeight: "1.5" }}>
                {deleteLeadId === "bulk"
                  ? `Delete ${selectedLeads.length} selected leads permanently?`
                  : "Are you sure you want to delete this lead permanently?"}
              </p>
            </div>

            <div
              style={{
                padding: "12px 20px 16px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                backgroundColor: "#FAFAFA",
                borderTop: "1px solid #F3F4F6",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() => setShowDeleteLeadModal(false)}
                style={{
                  padding: "6px 14px",
                  backgroundColor: "#fff",
                  border: "1px solid #D1D5DB",
                  borderRadius: "6px",
                  fontWeight: "600",
                  color: "#374151",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteLead(false)}
                disabled={isDeletingLead}
                style={{
                  padding: "6px 14px",
                  backgroundColor: "#DC2626",
                  border: "none",
                  borderRadius: "6px",
                  fontWeight: "600",
                  color: "#fff",
                  cursor: isDeletingLead ? "not-allowed" : "pointer",
                  fontSize: "12px",
                  opacity: isDeletingLead ? 0.7 : 1,
                }}
              >
                {isDeletingLead ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

     
      {/* Floating Filter Bar */}
      {viewMode === "table" && showFloatingFilter && (
        <div
          style={{
            position: "fixed",
            bottom: "135px",
            right: "76px", // Next to the filter button (24px + 42px + 10px)
            backgroundColor: "#fff",
            borderRadius: "12px",
            padding: "8px 12px",
            display: "flex",
            gap: "8px",
            alignItems: "center",
            boxShadow:
              "0 10px 25px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)",
            border: "1px solid #E5E7EB",
            zIndex: 98,
          }}
        >
          <div style={{ position: "relative", minWidth: "220px" }}>
            <span
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9CA3AF",
                display: "flex",
              }}
            >
              <svg
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search leads by name, email or ph..."
              style={{
                width: "100%",
                padding: "8px 12px 8px 32px",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                outline: "none",
                fontSize: "12px",
                color: "#111827",
                boxSizing: "border-box",
              }}
            />
          </div>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              outline: "none",
              fontSize: "12px",
              backgroundColor: "#fff",
              color: "#374151",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <option value="">All Status</option>
            <option value="New">New</option>
            <option value="Contracted">Contracted</option>
            <option value="Interested">Interested</option>
            <option value="Qualified">Qualified</option>
            <option value="Lost">Lost</option>
            <option value="Converted">Converted</option>
            <option value="Others">Others</option>
          </select>

          <select
            value={selectedStage}
            onChange={(e) => setSelectedStage(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              outline: "none",
              fontSize: "12px",
              backgroundColor: "#fff",
              color: "#374151",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <option value="">All Stages</option>
            <option value="New Lead">New Lead</option>
            <option value="Qualified">Qualified</option>
            <option value="Proposal">Proposal</option>
            <option value="Negotiation">Negotiation</option>
            <option value="Won">Won</option>
            <option value="Lost">Lost</option>
            <option value="Closed">Closed</option>
          </select>

          <button
            onClick={() => {
              setSearchTerm("");
              setSelectedStatus("");
              setSelectedStage("");
              setSelectedWidget("total");
              setRefreshTrigger((prev) => prev + 1);
              setIsRefreshing(true);
              setTimeout(() => setIsRefreshing(false), 500);
              toast.success("Filters reset and data refreshed");
            }}
            style={{
              padding: "7px",
              backgroundColor: "#fff",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              color: "#6B7280",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
            title="Reset Filters"
          >
              <svg
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
                style={{
                  transform: isRefreshing ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.5s ease",
                }}
              >
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
            </button>
        </div>
      )}

      {/* Floating View Toggle Bar */}
      {showFloatingView && (
        <div
          style={{
            position: "fixed",
            bottom: "80px",
            right: "76px",
            backgroundColor: "#fff",
            borderRadius: "12px",
            padding: "8px",
            display: "flex",
            gap: "8px",
            alignItems: "center",
            boxShadow:
              "0 10px 25px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)",
            border: "1px solid #E5E7EB",
            zIndex: 98,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "8px",
              backgroundColor: "#F3F4F6",
              padding: "4px",
              borderRadius: "8px",
            }}
          >
            <button
              onClick={() => setViewMode("kanban")}
              style={{
                padding: "6px 12px",
                backgroundColor: viewMode === "kanban" ? "#fff" : "transparent",
                border: "none",
                borderRadius: "6px",
                color: viewMode === "kanban" ? "#5B21B6" : "#6B7280",
                fontSize: "13px",
                fontWeight: viewMode === "kanban" ? "600" : "500",
                cursor: "pointer",
                boxShadow:
                  viewMode === "kanban" ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
              }}
            >
              Kanban View
            </button>
            <button
              onClick={() => setViewMode("table")}
              style={{
                padding: "6px 12px",
                backgroundColor: viewMode === "table" ? "#fff" : "transparent",
                border: "none",
                borderRadius: "6px",
                color: viewMode === "table" ? "#5B21B6" : "#6B7280",
                fontSize: "13px",
                fontWeight: viewMode === "table" ? "600" : "500",
                cursor: "pointer",
                boxShadow:
                  viewMode === "table" ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
              }}
            >
              Table View
            </button>
          </div>
        </div>
      )}

      {/* Floating Column Toggle Menu */}
    {viewMode === "table" && showColumnToggle && (
  <div
    style={{
      position: "fixed",
      bottom: "190px",
      right: "76px",
      backgroundColor: "#fff",
      borderRadius: "12px",
      padding: "14px",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      boxShadow:
        "0 10px 25px rgba(0,0,0,0.12), 0 4px 6px rgba(0,0,0,0.05)",
      border: "1px solid #E5E7EB",
      zIndex: 98,
      width: "320px",
    }}
  >
    <style>{`
      .custom-column-scrollbar::-webkit-scrollbar {
        width: 6px;
      }
      .custom-column-scrollbar::-webkit-scrollbar-button {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
      }
      .custom-column-scrollbar::-webkit-scrollbar-thumb {
        background-color: #CBD5E1;
        border-radius: 9999px;
      }
      .custom-column-scrollbar::-webkit-scrollbar-thumb:hover {
        background-color: #94A3B8;
      }
      .custom-column-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }
    `}</style>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "8px",
        paddingBottom: "8px",
        borderBottom: "1px solid #F3F4F6",
      }}
    >
      <span
        style={{ fontSize: "13px", fontWeight: "600", color: "#374151" }}
      >
        Customize Columns
      </span>
      {canCreateLead&&<button
        onClick={() => setShowCustomColumnManager(true)}
        style={{
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
          color: "#7C3AED",
          display: "flex",
          alignItems: "center",
          padding: "4px",
        }}
        title="Add Custom Column"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
      </button>}
    </div>
    <div
      className="custom-column-scrollbar"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        maxHeight: "320px",
        overflowY: "auto",
        paddingRight: "8px",
      }}
    >
      {(() => {
        const allColsList = Array.from(new Set([
          ...columnOrder,
          ...Object.keys(defaultColumnNames),
          ...Object.keys(visibleColumns),
          ...customFields.map(f => `custom_${f.fieldKey}`)
        ]));
        return allColsList.map((col, idx) => {
          const isCustomField = col.startsWith('custom_');
          const fieldKey = isCustomField ? col.replace('custom_', '') : null;
          const customField = customFields.find(f => f.fieldKey === fieldKey);
          const isBeingDragged = draggedColIndex === idx;
          const isOver = dragOverColIndex === idx;
          
          return (
            <div
              key={col}
              draggable={true}
              onDragStart={(e) => {
                setDraggedColIndex(idx);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", col);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (dragOverColIndex !== idx) {
                  setDragOverColIndex(idx);
                }
              }}
              onDragLeave={() => {
                if (dragOverColIndex === idx) {
                  setDragOverColIndex(null);
                }
              }}
              onDragEnd={() => {
                setDraggedColIndex(null);
                setDragOverColIndex(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (draggedColIndex !== null && draggedColIndex !== idx) {
                  moveColumnItem(draggedColIndex, idx);
                }
                setDraggedColIndex(null);
                setDragOverColIndex(null);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "5px 8px",
                borderRadius: "6px",
                cursor: isBeingDragged ? "grabbing" : "grab",
                backgroundColor: isBeingDragged
                  ? "#F3E8FF"
                  : isOver
                  ? "#FAF5FF"
                  : "#FFFFFF",
                border: isBeingDragged
                  ? "1px dashed #7C3AED"
                  : isOver
                  ? "1px solid #A855F7"
                  : "1px solid #F3F4F6",
                opacity: isBeingDragged ? 0.5 : 1,
                boxShadow: isBeingDragged
                  ? "0 4px 6px -1px rgba(124, 58, 237, 0.15)"
                  : "none",
                transition: "all 0.15s ease",
                userSelect: "none"
              }}
            >
              <div
                style={{
                  cursor: "grab",
                  color: "#9CA3AF",
                  fontSize: "14px",
                  lineHeight: "1",
                  userSelect: "none",
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0
                }}
                title="Drag row to reorder"
              >
                ⋮⋮
              </div>
              <input
                type="checkbox"
                checked={visibleColumns[col] !== false}
                onMouseDown={(e) => e.stopPropagation()}
                onChange={(e) =>
                  setVisibleColumns((prev) => ({
                    ...prev,
                    [col]: e.target.checked,
                  }))
                }
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "4px",
                  border: "1px solid #D1D5DB",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
                title="Show/Hide Column"
              />
              <input
                type="text"
                value={
                  columnNames[col]
                    ? columnNames[col].toUpperCase()
                    : defaultColumnNames[col]
                    ? defaultColumnNames[col].toUpperCase()
                    : isCustomField
                    ? (customField?.fieldName || fieldKey).replace(/_/g, ' ').toUpperCase()
                    : col.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim().toUpperCase()
                }
                onMouseDown={(e) => e.stopPropagation()}
                onChange={(e) =>
                  setColumnNames((prev) => ({
                    ...prev,
                    [col]: e.target.value.toUpperCase(),
                  }))
                }
                style={{
                  flex: 1,
                  padding: "4px 8px",
                  borderRadius: "4px",
                  border: "1px solid #E5E7EB",
                  fontSize: "12px",
                  outline: "none",
                  color: "#4B5563",
                  backgroundColor: "#FFFFFF",
                  cursor: "text"
                }}
                title="Rename Column"
              />
            </div>
          );
        });
      })()}
      {isAddingColumn && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginTop: "4px",
          }}
        >
          <div style={{ width: "14px", height: "14px", flexShrink: 0 }} />
          <input
            type="text"
            value={draftColumnName}
            onChange={(e) => setDraftColumnName(e.target.value)}
            placeholder="Enter column name..."
            style={{
              flex: 1,
              padding: "4px 8px",
              borderRadius: "4px",
              border: "1px solid #3B82F6",
              fontSize: "12px",
              outline: "none",
              color: "#4B5563",
            }}
            autoFocus
          />
          <button
            onClick={() => {
              if (draftColumnName.trim()) {
                const newId = "customCol_" + Date.now();
                setVisibleColumns((prev) => ({ ...prev, [newId]: true }));
                setColumnNames((prev) => ({
                  ...prev,
                  [newId]: draftColumnName.trim(),
                }));
                setIsAddingColumn(false);
              }
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#10B981",
              padding: "2px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Save Column"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </button>
          <button
            onClick={() => setIsAddingColumn(false)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#EF4444",
              padding: "2px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Cancel"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      )}
    </div>
  </div>
)}

{/* Delete Field Confirmation Modal */}
{showDeleteFieldModal && fieldToDelete && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 3000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }}>
    <div style={{
      backgroundColor: '#fff',
      borderRadius: '12px',
      width: '450px',
      maxWidth: '95%',
      padding: '32px 24px 24px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      textAlign: 'center'
    }}>
      <div style={{
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        backgroundColor: '#FEE2E2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 16px'
      }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6"></polyline>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          <line x1="10" y1="11" x2="10" y2="17"></line>
          <line x1="14" y1="11" x2="14" y2="17"></line>
        </svg>
      </div>
      
      <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
        Delete Custom Field?
      </h3>
      
      <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6B7280' }}>
        Are you sure you want to permanently delete the field
      </p>
      <p style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
        "{fieldToDelete.fieldName}"?
      </p>
      

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
        <button
          onClick={() => {
            setShowDeleteFieldModal(false);
            setFieldToDelete(null);
          }}
          style={{
            padding: '8px 24px',
            backgroundColor: '#F3F4F6',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            color: '#374151',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            if (!fieldToDelete) return;
            
            setIsDeletingField(true);
            try {
              const axiosInstance = createAxiosInstance();
              const apiUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";
              
              const response = await axiosInstance.delete(`${apiUrl}/leads/field/${fieldToDelete._id}`);
              
              if (response.data && response.data.success) {
                toast.success('Custom field deleted successfully!');
                
                // Remove from custom fields list
                setCustomFields(prev => prev.filter(f => f._id !== fieldToDelete._id));
                
                // Remove from visible columns
                const colKey = `custom_${fieldToDelete.fieldKey}`;
                setVisibleColumns(prev => {
                  const newColumns = { ...prev };
                  delete newColumns[colKey];
                  return newColumns;
                });
                setColumnNames(prev => {
                  const newNames = { ...prev };
                  delete newNames[colKey];
                  return newNames;
                });
                
                // Refresh the leads data
                setRefreshTrigger(prev => prev + 1);
                
                setShowDeleteFieldModal(false);
                setFieldToDelete(null);
              } else {
                toast.error(response.data?.message || 'Failed to delete custom field');
              }
            } catch (error) {
              console.error('Failed to delete custom field:', error);
              toast.error(error.response?.data?.message || 'Failed to delete custom field');
            } finally {
              setIsDeletingField(false);
            }
          }}
          disabled={isDeletingField}
          style={{
            padding: '8px 24px',
            backgroundColor: '#EF4444',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            color: '#fff',
            cursor: isDeletingField ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            opacity: isDeletingField ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {isDeletingField ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <line x1="12" y1="2" x2="12" y2="6"></line>
                <line x1="12" y1="18" x2="12" y2="22"></line>
                <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                <line x1="2" y1="12" x2="6" y2="12"></line>
                <line x1="18" y1="12" x2="22" y2="12"></line>
                <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
              </svg>
              Deleting...
            </>
          ) : (
            'Delete Permanently'
          )}
        </button>
      </div>
    </div>
  </div>
)}
      {/* Floating Column Toggle Button */}
      {viewMode === "table" && (
        <button
          onClick={() => {
            setShowColumnToggle(!showColumnToggle);
            if (showFloatingFilter) setShowFloatingFilter(false);
            if (showFloatingView) setShowFloatingView(false);
          }}
          style={{
            position: "fixed",
            bottom: "190px",
            right: "24px",
            width: "42px",
            height: "42px",
            backgroundColor: "#6366F1", // Indigo to match theme
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: "0 4px 12px rgba(99, 102, 241, 0.4)",
            border: "none",
            cursor: "pointer",
            zIndex: 99,
            transition: "transform 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.transform = "scale(1.05)")
          }
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        >
          {showColumnToggle ? (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ) : (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="9" y1="3" x2="9" y2="21"></line>
            </svg>
          )}
        </button>
      )}

      {/* Floating View Button */}
      <button
        onClick={() => {
          setShowFloatingView(!showFloatingView);
          if (showFloatingFilter) setShowFloatingFilter(false);
          if (showColumnToggle) setShowColumnToggle(false);
        }}
        style={{
          position: "fixed",
          bottom: "80px",
          right: "24px",
          width: "42px",
          height: "42px",
          backgroundColor: "#8B5CF6",
          borderRadius: "50%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          boxShadow: "0 4px 12px rgba(139, 92, 246, 0.4)",
          border: "none",
          cursor: "pointer",
          zIndex: 99,
          transition: "transform 0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        {showFloatingView ? (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
          </svg>
        )}
      </button>

      {/* Floating Sort & Filter Button */}
      {viewMode === "table" && (
        <button
          onClick={() => {
            setShowFloatingFilter(!showFloatingFilter);
            if (showFloatingView) setShowFloatingView(false);
            if (showColumnToggle) setShowColumnToggle(false);
          }}
          style={{
            position: "fixed",
            bottom: "135px",
            right: "24px",
            width: "42px",
            height: "42px",
            backgroundColor: "#3B82F6",
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
            border: "none",
            cursor: "pointer",
            zIndex: 99,
            transition: "transform 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.transform = "scale(1.05)")
          }
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        >
          {showFloatingFilter ? (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ) : (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
            </svg>
          )}
        </button>
      )}

      <ImportLeadsModal
  isOpen={showImportModal}
  onClose={() => setShowImportModal(false)}
  tenantId={access?.tenantId}
  customFields={customFields} // Pass customFields from CRM
  columnNames={columnNames}
  onImportSuccess={(newLeads) => {
    if (newLeads && newLeads.length > 0) {
      setLeads((prev) => [...newLeads, ...prev]);
    }
    setColumnNamesRefresh(prev => !prev);
    setRefreshTrigger((prev) => prev + 1);
  }}
/>
      <CustomColumnManager
  isOpen={showCustomColumnManager}
  onClose={() => setShowCustomColumnManager(false)}
  tenantId={access?.tenantId}
  visibleColumns={visibleColumns}
  columnNames={columnNames}
  setVisibleColumns={setVisibleColumns}
  setColumnNames={setColumnNames}
  onColumnsUpdate={() => {
    setColumnNamesRefresh(!columnRefresh)
    setRefreshTrigger(prev => prev + 1);
  }}
/>
    </div>
  );
}
